/* mien-feed.js — masonry feed, tile (product) pages, and the closet.
   vanilla, no deps. shared by sections/mien-feed.liquid, mien-search.liquid,
   mien-tile-page.liquid and mien-closet.liquid.
   round 4: tiles are real links to tile pages (the drawer is retired —
   assets/mien-drawer-retired.js keeps that code); masonry is ONE css grid with
   js-set row spans (grid-auto-rows 4px) so gutters are identical both axes. */
(function () {
  'use strict';

  /* ============================================================
     closet storage — the ONLY code that touches persistence.
     two backends behind one interface (get/has/add/remove):
       ANONYMOUS — localStorage "mien_closet" (no login wall on ♡)
       LOGGED IN — supabase closet_items via window.MIEN (the same
         session as community; helpers live in mien-supabase.js)
     merge on login: local handles upsert into closet_items (dedup),
     localStorage clears, supabase becomes source of truth.
     UI listens for 'mien:closet' (detail: {handles, mode}).
     ============================================================ */
  var MienCloset = (function () {
    var KEY = 'mien_closet';
    var SNAP_KEY = 'mien_closet_snap';   /* handle -> {title, brand, price, image_url} (anon only) */
    var mode = 'local';
    var cache;
    var snaps = {};
    var lastUid = null;

    function localRead() {
      try {
        var v = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(v) ? v : [];
      } catch (e) { return []; }
    }
    function localWrite(v) {
      try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode — session-only */ }
    }
    function snapRead() {
      try { var v = JSON.parse(localStorage.getItem(SNAP_KEY) || '{}'); return (v && typeof v === 'object') ? v : {}; }
      catch (e) { return {}; }
    }
    function snapWrite() {
      try { localStorage.setItem(SNAP_KEY, JSON.stringify(snaps)); } catch (e) {}
    }
    /* pending saves — a ♡ tapped while signed out that routed to auth (signup / password reset)
       instead of a silent local save. persisted so it survives an email round-trip (e.g. reset),
       then folded into the merge on the next sign-in and cleared. entries expire after 2h. */
    var PEND_KEY = 'mien_pending_saves', PEND_TTL = 2 * 60 * 60 * 1000;
    function pendRead() {
      try {
        var v = JSON.parse(localStorage.getItem(PEND_KEY) || '[]');
        if (!Array.isArray(v)) return [];
        var now = Date.now();
        return v.filter(function (p) { return p && p.handle && (!p.ts || (now - p.ts) < PEND_TTL); });
      } catch (e) { return []; }
    }
    function pendClear() { try { localStorage.removeItem(PEND_KEY); } catch (e) {} }
    function stashPending(handle, snap) {
      if (!handle) return;
      try {
        var v = pendRead().filter(function (p) { return p.handle !== handle; });
        v.push({ handle: handle, snap: snap || null, ts: Date.now() });
        localStorage.setItem(PEND_KEY, JSON.stringify(v));
      } catch (e) {}
    }
    function fromRows(rows) {
      cache = []; snaps = {};
      (rows || []).forEach(function (r) {
        cache.push(r.product_handle);
        snaps[r.product_handle] = { title: r.title, brand: r.brand, price: r.price, image_url: r.image_url, product_gone: r.product_gone };
      });
    }
    function emit() {
      document.dispatchEvent(new CustomEvent('mien:closet', { detail: { handles: cache.slice(), mode: mode } }));
    }
    function sbReady() {
      return !!(window.MIEN && MIEN.ready && MIEN.ready() && MIEN.closetListFull);
    }
    function resync() {
      if (mode !== 'account' || !sbReady()) return;
      MIEN.closetListFull().then(function (rows) {
        if (mode !== 'account') return;
        fromRows(rows);
        emit();
      }).catch(function () {});
    }

    cache = localRead();
    snaps = snapRead();

    function get() { return cache.slice(); }
    function snap(handle) { return snaps[handle] || null; }
    function has(handle) { return cache.indexOf(handle) !== -1; }
    function add(handle, snapshot) {
      if (!handle || has(handle)) return;
      cache.push(handle);
      if (snapshot) snaps[handle] = snapshot;
      if (mode === 'account' && sbReady()) {
        MIEN.closetAdd(handle, snapshot)
          .then(function (r) { if (r && r.error) resync(); })
          .catch(function () { resync(); });
      } else {
        localWrite(cache);
        snapWrite();
      }
      emit();
    }
    function remove(handle) {
      if (!has(handle)) return;
      cache = cache.filter(function (h) { return h !== handle; });
      delete snaps[handle];
      if (mode === 'account' && sbReady()) {
        MIEN.closetRemove(handle)
          .then(function (r) { if (r && r.error) resync(); })
          .catch(function () { resync(); });
      } else {
        localWrite(cache);
        snapWrite();
      }
      emit();
    }

    function adopt(sess) {
      if (!sess) {
        mode = 'local';
        cache = localRead();
        snaps = snapRead();
        emit();
        return;
      }
      var locals = localRead();
      var localSnaps = snapRead();
      var items = locals.map(function (h) {
        var s = localSnaps[h] || {};
        return { handle: h, title: s.title, brand: s.brand, price: s.price, image_url: s.image_url };
      });
      /* fold in any pending saves (a ♡ that routed to auth before this sign-in, incl. across a
         password-reset email round-trip) so they complete now. */
      var pend = pendRead(), have = {};
      items.forEach(function (it) { have[it.handle] = 1; });
      pend.forEach(function (p) {
        if (have[p.handle]) return; have[p.handle] = 1;
        var s = p.snap || {};
        items.push({ handle: p.handle, title: s.title, brand: s.brand, price: s.price, image_url: s.image_url });
      });
      var merge = items.length ? MIEN.closetMerge(items) : Promise.resolve(null);
      Promise.resolve(merge)
        .then(function (r) {
          /* merge failed → stay local, lose nothing */
          if (r && r.error) throw r.error;
          return MIEN.closetListFull();
        })
        .then(function (rows) {
          mode = 'account';
          fromRows(rows);
          if (locals.length) { localWrite([]); try { localStorage.removeItem(SNAP_KEY); } catch (e) {} }
          pendClear();
          emit();
        })
        .catch(function () {});
    }
    function onAuth(sess) {
      var uid = sess && sess.user ? sess.user.id : null;
      if (uid === lastUid) return;
      lastUid = uid;
      adopt(sess);
    }

    var tries = 0;
    (function boot() {
      if (!sbReady()) { if (++tries < 60) setTimeout(boot, 150); return; }
      MIEN.session().then(onAuth).catch(function () {});
      MIEN.onChange(onAuth);
    })();

    return { KEY: KEY, get: get, snap: snap, has: has, add: add, remove: remove, stashPending: stashPending, mode: function () { return mode; } };
  })();
  window.MienCloset = MienCloset;

  /* ============================================================
     signal capture — write-only behavioral events for a FUTURE
     recommender. this round does NOT rank or reorder anything; it
     only records. everything here is fire-and-forget and fully
     wrapped: a logging failure must never block a nav, save, or
     checkout. skipped entirely under Do-Not-Track / Global Privacy
     Control. batches to the supabase `events` table (insert-only RLS).
     ============================================================ */
  var MienSignal = (function () {
    var OFF = { log: function () {}, observe: function () {}, flush: function () {}, disabled: true };
    try {
      if (navigator.globalPrivacyControl === true) return OFF;
      var dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
      if (dnt === '1' || dnt === 'yes' || dnt === true) return OFF;
    } catch (e) {}

    function uuid() {
      try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
      try {
        var b = crypto.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
        var h = []; for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
        return h.slice(0,4).join('') + '-' + h.slice(4,6).join('') + '-' + h.slice(6,8).join('') + '-' + h.slice(8,10).join('') + '-' + h.slice(10,16).join('');
      } catch (e) { return 'sess-' + Date.now() + '-' + Math.floor(Math.random() * 1e9); }
    }

    /* anon session id — persisted so behavior stitches across page loads */
    var sid;
    try { sid = localStorage.getItem('mien_sid'); if (!sid) { sid = uuid(); localStorage.setItem('mien_sid', sid); } }
    catch (e) { sid = uuid(); }

    /* impression dedupe — per handle per session, persisted + capped */
    var IMP_KEY = 'mien_imp', impressed = {};
    try {
      var raw = JSON.parse(localStorage.getItem(IMP_KEY) || '[]');
      if (Array.isArray(raw)) raw.forEach(function (h) { impressed[h] = 1; });
    } catch (e) {}
    function markImpressed(h) {
      impressed[h] = 1;
      try {
        var keys = Object.keys(impressed);
        if (keys.length > 4000) keys = keys.slice(keys.length - 4000);
        localStorage.setItem(IMP_KEY, JSON.stringify(keys));
      } catch (e) {}
    }

    /* auth cache — user_id stamping + REST authorization + claim-on-login */
    var uid = null, token = null, claimed = false;
    function claimSession() {
      if (claimed || !uid || !(window.MIEN && MIEN.sb)) return;
      try { MIEN.sb.rpc('claim_session', { p_session_id: sid }).then(function () { claimed = true; }).catch(function () {}); }
      catch (e) {}
    }
    (function bootAuth() {
      var t = 0;
      (function poll() {
        if (!(window.MIEN && MIEN.ready && MIEN.ready())) { if (++t < 60) setTimeout(poll, 150); return; }
        function apply(s) {
          var was = uid;
          uid = (s && s.user) ? s.user.id : null;
          token = s ? s.access_token : null;
          if (uid && uid !== was) { claimed = false; claimSession(); }
        }
        MIEN.session().then(apply).catch(function () {});
        MIEN.onChange(apply);
      })();
    })();

    var queue = [];
    function flush(keepalive) {
      if (!queue.length) return;
      var batch = queue.splice(0, queue.length);
      /* normal path: let the supabase-js client insert — it carries the right
         apikey + auth (anon vs the user's jwt) so RLS attribution just works */
      if (!keepalive && window.MIEN && MIEN.sb) {
        try { MIEN.sb.from('events').insert(batch).then(function () {}, function () {}); return; }
        catch (e) { /* fall through to raw fetch */ }
      }
      /* unload path: supabase-js can't guarantee delivery as the page goes away,
         so hit the REST endpoint with keepalive (mirrors the client's headers) */
      var base = (window.MIEN && MIEN.url) ? MIEN.url : '';
      var apikey = (window.MIEN && MIEN.key) ? MIEN.key : '';
      if (!base || !apikey) return; /* supabase not wired yet — drop silently */
      try {
        fetch(base + '/rest/v1/events', {
          method: 'POST',
          headers: {
            'apikey': apikey,
            'Authorization': 'Bearer ' + (token || apikey),
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify(batch),
          keepalive: true,
          cache: 'no-store'
        }).catch(function () {});
      } catch (e) { /* swallow — never surface a logging error */ }
    }

    function log(type, handle, extra) {
      try {
        /* only claim a user_id when we can also authenticate it (RLS: anon rows must be null) */
        var withUser = (uid && token) ? uid : null;
        var row = { session_id: sid, user_id: withUser, product_handle: handle || null, event_type: type };
        if (extra && typeof extra.duration_ms === 'number') row.duration_ms = extra.duration_ms;
        queue.push(row);
        if (queue.length >= 25) flush(false);
      } catch (e) {}
    }

    try { setInterval(function () { flush(false); }, 5000); } catch (e) {}
    window.addEventListener('pagehide', function () { flush(true); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush(true);
    });

    /* impressions — one shared observer; a tile must hold >=50% visible for >=1s */
    var io = null, timers = null;
    function ensureObserver() {
      if (io || !('IntersectionObserver' in window)) return;
      timers = new WeakMap();
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var el = en.target, h = el.getAttribute('data-handle');
          if (!h) return;
          if (en.isIntersecting && en.intersectionRatio >= 0.5) {
            if (impressed[h] || timers.get(el)) return;
            var timer = setTimeout(function () {
              timers.delete(el);
              if (impressed[h]) return;
              markImpressed(h);
              log('impression', h);
              try { if (io) io.unobserve(el); } catch (e) {}
            }, 1000);
            timers.set(el, timer);
          } else {
            var t2 = timers.get(el);
            if (t2) { clearTimeout(t2); timers.delete(el); }
          }
        });
      }, { threshold: [0, 0.5, 1] });
    }
    function observe(el) {
      try {
        if (!el) return;
        var h = el.getAttribute('data-handle');
        if (!h || impressed[h]) return;
        ensureObserver();
        if (io) io.observe(el);
      } catch (e) {}
    }

    return { log: log, observe: observe, flush: flush, sid: function () { return sid; }, disabled: false };
  })();
  window.MienSignal = MienSignal;

  /* tier-at-save memory → the closet's "now available" mark. handle → 'featured'|'buyable' */
  var TierCache = {
    KEY: 'mien_closet_tier',
    _read: function () {
      try {
        var v = JSON.parse(localStorage.getItem(this.KEY) || '{}');
        return (v && typeof v === 'object') ? v : {};
      } catch (e) { return {}; }
    },
    get: function (h) { return this._read()[h] || null; },
    set: function (h, tier) {
      var v = this._read();
      v[h] = tier;
      try { localStorage.setItem(this.KEY, JSON.stringify(v)); } catch (e) {}
    }
  };

  /* ---- helpers ---- */
  function lc(s) { return (s || '').toLowerCase(); }
  /* native/collective — the js twin of snippets/mien-is-native (tag pair, never a vendor
     name): a tile you buy ON mien rather than tap out of. data-tags is emitted downcased. */
  function isNativeTile(t) {
    var tags = t.getAttribute('data-tags') || '';
    return tags.indexOf('source:collective') >= 0 || tags.indexOf('shopify collective') >= 0;
  }
  /* in stock too — a sold-out native keeps its badge in the normal flow and never
     spends a guaranteed slot (.mfeed-oos without .mfeed-low = the sold-out badge) */
  function nativeInStock(t) {
    return isNativeTile(t) && !t.querySelector('.mfeed-oos:not(.mfeed-low)');
  }
  function money(cents, sym) { return (sym || '$') + (cents / 100).toFixed(2); }
  function imgUrl(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  function shuffle(a) { /* Fisher-Yates */
    for (var i = a.length - 1; i > 0; i--) {
      var j = (Math.random() * (i + 1)) | 0, t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }
  /* seeded prng (mulberry32) — the feed arrangement is drawn from THIS, not Math.random,
     so the order is stable for the whole session (infinite scroll can never dupe or skip
     a tile mid-scroll). the seed lives in sessionStorage → new session, new arrangement. */
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function feedSessionSeed() {
    var s = 0;
    try { s = parseInt(sessionStorage.getItem('mien_feed_seed'), 10) || 0; } catch (e) {}
    if (!s) {
      s = 1 + ((Math.random() * 2147483646) | 0);
      try { sessionStorage.setItem('mien_feed_seed', String(s)); } catch (e) {}
    }
    return s;
  }
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.mhd-n').forEach(function (b) { b.textContent = cart.item_count; });
        /* the header cart hides itself while the bag is empty (mien-header .is-empty) —
           reveal it the moment something lands in it, hide it again when it empties */
        document.querySelectorAll('.mhd-cart').forEach(function (a) {
          a.classList.toggle('is-empty', !cart.item_count);
        });
      })
      .catch(function () {});
  }
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js').then(function (r) {
      if (r.status === 404) return { gone: true, handle: handle };
      if (!r.ok) return null;
      return r.json();
    }).catch(function () { return null; });
  }
  function productBuyable(p) {
    var tags = (p.tags || []).map(lc);
    return tags.indexOf('tier:featured') === -1 && !!p.available;
  }

  /* ============================================================
     masonry — one grid; each tile spans rows from its aspect
     ratio, so vertical gutters equal the column gap exactly.
     ============================================================ */
  var ROW = 4; /* must match grid-auto-rows in mien.css v56 */

  /* snap a raw ratio to the fixed set (3:4/4:5/1:1/5:4/4:3); ties lean portrait.
     mirrors the Liquid snapping in snippets/mien-feed-tile.liquid for js-built tiles */
  function snapRatio(r) {
    if (!r || r <= 0) return 0.8;
    if (r <= 0.775) return 0.75;
    if (r <= 0.90) return 0.8;
    if (r <= 1.125) return 1;
    if (r <= 1.2917) return 1.25;
    return 1.3333;
  }

  function ratioOf(el) {
    var r = (el.style.aspectRatio || el.getAttribute('data-ratio') || '').trim();
    if (!r) return 0;
    if (r.indexOf('/') > -1) {
      var p = r.split('/');
      return (parseFloat(p[0]) / parseFloat(p[1])) || 0;
    }
    return parseFloat(r) || 0;
  }
  function gapOf(grid) {
    var g = parseFloat(getComputedStyle(grid).columnGap);
    return isNaN(g) ? 8 : g;
  }
  function layoutTile(el) {
    var grid = el.parentElement;
    if (!grid || !grid.classList.contains('is-masonry')) return;
    var w = el.offsetWidth;
    if (!w) return; /* not laid out yet — masonryInit's rAF + the grid ResizeObserver re-run this */
    var gap = gapOf(grid);
    /* the plate reserves its FULL height from css aspect-ratio (inline on .mfeed-plate + width/height
       on the img), so this is exact BEFORE the image loads. we take max(measured, ratio-derived) so a
       tile is NEVER under-sized — an under-sized span is exactly what let tall tiles overflow their
       grid area and overlap the next row on a cold cache. */
    var ratio = ratioOf(el);
    var label = el.querySelector('.mfeed-label');
    var labelH = (label && label.offsetHeight) ? label.offsetHeight : 0;
    /* a revealed curator's note is real height under the plate — counted here for the same
       reason the label is: an under-sized span lets a tall tile overlap the next row. */
    var note = el.querySelector('.mfeed-curator:not([hidden])');
    if (note && note.offsetHeight) labelH += note.offsetHeight;
    var floorH = ratio > 0 ? (w / ratio) + labelH : 0;
    var h = Math.max(el.offsetHeight || 0, floorH);
    if (!h) return;
    el.style.gridRowEnd = 'span ' + Math.max(1, Math.ceil((h + gap) / ROW));
    /* safety net: recompute once the image truly loads (aspect-ratio should make this a no-op) */
    var img = el.querySelector('.mfeed-plate > img') || el.querySelector('img');
    if (img && !img.complete && !img.getAttribute('data-mll')) {
      img.setAttribute('data-mll', '1');
      img.addEventListener('load', function () { layoutTile(el); }, { once: true });
    }
  }
  function layoutGrid(grid) {
    [].forEach.call(grid.children, layoutTile);
  }

  /* ============================================================
     the curator's line (2026-08-16) — "mien pick" / "why it's here".
     the copy is rendered into every eligible tile by mien-note.liquid but starts
     HIDDEN; this is what decides which ones actually speak. rule: a brand gets its
     note on the FIRST tile of that brand you scroll past, and never again. said once
     it reads like a curator leaning over; said on all 179 twojeys tiles it reads like
     a caption template, and the feed stops being pictures.

     why here and not in liquid: the feed is seeded-shuffled and taste-ranked in the
     browser, so the server has no idea which tile lands first. noteApply is idempotent
     and reads only from noteSeen, so the pool rebuild can reset the map and re-apply
     over the very same nodes without notes doubling up or stranding.
     ============================================================ */
  /* STUBBED 2026-08-20 — the note is no longer rendered into tiles (see mien-feed-tile), so
     there is nothing to reveal. left in place rather than ripped out because it is called from
     four points in the render path (initial paint, shuffle, page-in, taste re-rank) and the
     rationing logic below is the part that took the longest to get right: once per brand, with
     a six-tile gap. un-stub by deleting this early return. */
  var NOTES_ON = false;
  var noteSeen = {};
  var noteSince = 99;
  /* second gate, on top of once-per-brand: at least this many tiles between notes. the top of
     the feed is deliberately brand-INTERLEAVED (see arrange), so "first tile of each brand"
     alone put a note on 13 of the first 24 — technically once each, visually a wall of text.
     a brand that loses its turn to the gap keeps its turn: it is not marked seen, so it speaks
     on its next tile instead. every brand still gets its one line, just never in a clump. */
  var NOTE_GAP = 6;
  function noteReset() { noteSeen = {}; noteSince = 99; }
  function noteApply(tile) {
    if (!NOTES_ON) return;
    var note = tile.querySelector('[data-mfeed-cur]');
    if (!note) { noteSince++; return; }
    var brand = (tile.getAttribute('data-brand') || '').trim();
    /* a per-product note (custom.mien_note) is hand-written for that one piece, so it ignores
       both gates — it is a note about THAT piece, not the brand's turn to talk. */
    var own = note.getAttribute('data-mfeed-cur') === 'own';
    var show = own || (brand && !noteSeen[brand] && noteSince >= NOTE_GAP);
    if (show) { noteSince = 0; if (!own) noteSeen[brand] = true; }
    else noteSince++;
    note.hidden = !show;
  }

  /* ============================================================
     cutout detection — tiles are full-bleed (object-fit: cover) by default;
     images whose EDGES are transparent or near-white (cutouts / white studio
     shots) get .is-cutout so css floats them on a soft off-white plate + multiply
     instead of showing a bare white box. samples a tiny canvas from a CORS probe;
     if the canvas taints (no CORS), it silently leaves the tile full-bleed.
     ============================================================ */
  var _cutCanvas = null;
  function detectCutout(tile) {
    if (!tile || tile.getAttribute('data-cutout-done')) return;
    var img = tile.querySelector('.mfeed-plate > img');
    if (!img) return;
    function run() {
      tile.setAttribute('data-cutout-done', '1');
      try {
        var probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = function () {
          try {
            var W = 18, H = 18;
            var c = _cutCanvas || (_cutCanvas = document.createElement('canvas'));
            c.width = W; c.height = H;
            var ctx = c.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(probe, 0, 0, W, H);
            var d = ctx.getImageData(0, 0, W, H).data, near = 0, total = 0;
            function edge(i) {
              var a = d[i + 3]; total++;
              if (a < 24) { near++; return; }                            /* transparent edge */
              if (d[i] > 232 && d[i + 1] > 232 && d[i + 2] > 232) near++; /* near-white edge */
            }
            var x, y;
            for (x = 0; x < W; x++) { edge(x * 4); edge(((H - 1) * W + x) * 4); }
            for (y = 0; y < H; y++) { edge((y * W) * 4); edge((y * W + (W - 1)) * 4); }
            if (total && near / total >= 0.7) {
              tile.classList.add('is-cutout');
              var g = tile.parentElement;
              if (g && g.classList.contains('is-masonry')) layoutTile(tile);
            }
          } catch (e) { /* tainted canvas — leave full-bleed */ }
        };
        probe.onerror = function () {};
        probe.src = img.currentSrc || img.src;
      } catch (e) {}
    }
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener('load', run, { once: true });
  }
  function masonryInit(grid) {
    grid.classList.add('is-masonry');
    layoutGrid(grid);
    /* run again after the browser has painted (widths resolved) + once more the next frame, so the
       very first cold-cache layout is correct without waiting on images */
    requestAnimationFrame(function () { layoutGrid(grid); requestAnimationFrame(function () { layoutGrid(grid); }); });
    /* safety net: re-lay out whenever the container WIDTH changes (rotation, resize, font swap).
       guarded on width so span changes (which alter height) never loop the observer. */
    if (window.ResizeObserver && !grid._mro) {
      grid._mw = grid.offsetWidth;
      grid._mro = new ResizeObserver(function () {
        var nw = grid.offsetWidth;
        if (nw && nw !== grid._mw) { grid._mw = nw; layoutGrid(grid); }
      });
      try { grid._mro.observe(grid); } catch (e) {}
    }
  }
  var rsT = null;
  window.addEventListener('resize', function () {
    clearTimeout(rsT);
    rsT = setTimeout(function () {
      document.querySelectorAll('.mfeed-grid.is-masonry').forEach(layoutGrid);
    }, 150);
  });

  /* ============================================================
     save buttons — any [data-mtp-save][data-handle] (tile pages).
     labels: primary "♡ save it"/"♥ saved" · secondary "♡ save"/"♥ saved".
     ============================================================ */
  function syncSaveButtons() {
    document.querySelectorAll('[data-mtp-save]').forEach(function (btn) {
      var h = btn.getAttribute('data-handle');
      if (!h) return;
      var saved = MienCloset.has(h);
      btn.classList.toggle('on', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      if (btn.hasAttribute('data-primary')) {
        btn.innerHTML = saved ? 'saved' : 'save it';
      } else {
        btn.innerHTML = saved ? 'saved' : 'save';
      }
    });
  }
  /* snapshot the tile's display fields at save time → closet_items columns, so the
     closet still renders if the product later 404s / unpublishes. */
  function snapFromFeedTile(btn) {
    var tile = (btn && btn.closest) ? btn.closest('.mfeed-tile') : null;
    if (!tile) return null;
    var img = tile.querySelector('img'), b = tile.querySelector('.mfeed-brand'), pr = tile.querySelector('.mfeed-price');
    return {
      title: img ? (img.getAttribute('alt') || '') : '',
      brand: b ? b.textContent.trim() : '',
      price: pr ? pr.textContent.trim() : '',
      image_url: img ? (img.currentSrc || img.src || '') : ''
    };
  }
  function snapFromTilePage() {
    var root = document.querySelector('[data-mtp]');
    if (!root) return null;
    var t = root.querySelector('.mtp-title'), v = root.querySelector('.mtp-vendor'),
        pr = root.querySelector('[data-mtp-price]'), img = root.querySelector('.mtp-gallery img');
    return {
      title: t ? t.textContent.trim() : '',
      brand: v ? v.textContent.trim().replace(/^featured\s*\/\s*/, '') : '',
      price: pr ? pr.textContent.trim() : '',
      image_url: img ? (img.currentSrc || img.src || '') : ''
    };
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-mtp-save]');
    if (!btn) return;
    var h = btn.getAttribute('data-handle');
    if (!h) return;
    if (MienCloset.has(h)) {
      MienCloset.remove(h);
      MienSignal.log('unsave', h);
      bumpSaveCount(h, -1);
    } else {
      MienCloset.add(h, snapFromTilePage());
      MienSignal.log('save', h);
      bumpSaveCount(h, 1);
      var tier = btn.getAttribute('data-tier');
      if (tier) TierCache.set(h, tier);
    }
  });
  document.addEventListener('mien:closet', syncSaveButtons);

  /* ============================================================
     feed tile hearts — the small ♥ overlaid on every masonry tile.
     saves/unsaves through the SAME closet backend (MienCloset →
     supabase closet_items when signed in). unlike the tile-page
     save button, an anonymous tap is a sign-up hook: it routes to
     the community auth instead of a silent local save.
     ============================================================ */
  var FeedAuth = { signedIn: null };
  (function bootFeedAuth() {
    var tries = 0;
    (function poll() {
      if (!(window.MIEN && MIEN.ready && MIEN.ready())) { if (++tries < 60) setTimeout(poll, 150); return; }
      MIEN.session().then(function (s) { FeedAuth.signedIn = !!(s && s.user); }).catch(function () { FeedAuth.signedIn = false; });
      MIEN.onChange(function (s) { FeedAuth.signedIn = !!(s && s.user); });
    })();
  })();

  /* signed-out heart → open the reusable signup modal as an OVERLAY (no navigation,
     no lost scroll, NEVER a 404). after auth completes, finish the save they attempted
     and the filled heart syncs via the mien:closet event. hard-fallback to the closet
     page only if the modal can't render. */
  function requestSignup(btn) {
    var h = btn.getAttribute('data-handle');
    if (!h) return;
    var snap = snapFromFeedTile(btn);
    /* persist the intent so it survives an email round-trip (e.g. a password reset started from
       the modal): MienCloset folds pending saves into the merge on the next sign-in. */
    if (MienCloset.stashPending) MienCloset.stashPending(h, snap);
    function complete() {
      FeedAuth.signedIn = true;
      if (!MienCloset.has(h)) { MienCloset.add(h, snap); MienSignal.log('save', h); bumpSaveCount(h, 1); }
    }
    if (window.MienAuthModal && MienAuthModal.open) {
      MienAuthModal.open({ mode: 'create', source: 'save', onSuccess: complete });
    } else {
      window.location.href = '/pages/account'; /* hard fallback — a real page, never a 404 */
    }
  }

  function heartToggle(btn) {
    var h = btn.getAttribute('data-handle');
    if (!h) return;
    function act() {
      if (MienCloset.has(h)) { MienCloset.remove(h); MienSignal.log('unsave', h); bumpSaveCount(h, -1); }
      else { MienCloset.add(h, snapFromFeedTile(btn)); MienSignal.log('save', h); bumpSaveCount(h, 1); }
    }
    /* already known to be signed in → toggle straight through the closet */
    if (MienCloset.mode() === 'account' || FeedAuth.signedIn === true) { act(); return; }
    /* known signed out → the signup modal */
    if (FeedAuth.signedIn === false) { requestSignup(btn); return; }
    /* auth not resolved yet — check the live session before deciding */
    if (window.MIEN && MIEN.ready && MIEN.ready()) {
      MIEN.session().then(function (s) {
        if (s && s.user) { FeedAuth.signedIn = true; act(); }
        else { FeedAuth.signedIn = false; requestSignup(btn); }
      }).catch(function () { requestSignup(btn); });
    } else {
      requestSignup(btn);
    }
  }

  function syncFeedHearts(root) {
    (root || document).querySelectorAll('[data-mfeed-heart]').forEach(function (btn) {
      var h = btn.getAttribute('data-handle');
      if (!h) return;
      var saved = MienCloset.has(h);
      btn.classList.toggle('on', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-mfeed-heart]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    heartToggle(btn);
  });
  document.addEventListener('mien:closet', function () { syncFeedHearts(); });

  /* tap → tile page: log, then let the link navigate (never blocked) */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-mfeed-heart]')) return;
    var plate = e.target.closest('.mfeed-plate');
    if (!plate) return;
    var tile = plate.closest('.mfeed-tile');
    var h = tile && tile.getAttribute('data-handle');
    if (h) MienSignal.log('tap', h);
  });

  /* ============================================================
     public save counts — the heart count = how many closets hold a handle.
     get_tile_save_counts(handles) is anon-callable; batched per render chunk
     (well under the 200 cap). feed hearts hide the digit at 0 (no reserved
     space → zero layout shift); tile pages always show it (incl. 0). the
     caller's own save optimistically bumps the number ±1.
     ============================================================ */
  function sbClient() { return (window.MIEN && MIEN.sb) ? MIEN.sb : null; }
  function setFeedCount(el, n) {
    n = Math.max(0, n | 0);
    el.setAttribute('data-count', n);
    if (n >= 1) { el.textContent = n; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }
  function setTileLikes(el, n) {
    n = Math.max(0, n | 0);
    el.setAttribute('data-count', n);
    el.textContent = n;
    /* show the like line only when there's at least one — a bold "0" just advertises emptiness */
    var wrap = el.closest ? el.closest('.mtp-likes') : null;
    if (wrap) wrap.hidden = (n < 1);
  }
  function applyFeedCounts(map) {
    Object.keys(map).forEach(function (h) {
      var n = map[h] || 0;
      document.querySelectorAll('.mfeed-tile[data-handle="' + h + '"] [data-mfeed-count]').forEach(function (el) { setFeedCount(el, n); });
    });
  }
  function fetchSaveCounts(handles, cb) {
    var client = sbClient();
    if (!client || !handles || !handles.length) return;
    var seen = {}, uniq = [];
    handles.forEach(function (h) { if (h && !seen[h]) { seen[h] = 1; uniq.push(h); } });
    for (var i = 0; i < uniq.length; i += 200) {
      (function (chunk) {
        try {
          client.rpc('get_tile_save_counts', { p_handles: chunk }).then(function (r) {
            var map = {};
            if (r && !r.error && r.data) r.data.forEach(function (row) { map[row.product_handle] = row.save_count; });
            /* zero-fill queried-but-absent handles so they're marked fetched (not re-queried) */
            chunk.forEach(function (h) { if (!(h in map)) map[h] = 0; });
            cb(map);
          }, function () {});
        } catch (e) {}
      })(uniq.slice(i, i + 200));
    }
  }
  /* fetch counts for every tile in `root` that hasn't been counted yet (incremental per batch) */
  function countFeedBatch(root) {
    var handles = [];
    (root || document).querySelectorAll('.mfeed-tile[data-handle]').forEach(function (t) {
      var c = t.querySelector('[data-mfeed-count]');
      if (c && !c.getAttribute('data-count')) handles.push(t.getAttribute('data-handle'));
    });
    if (handles.length) fetchSaveCounts(handles, applyFeedCounts);
    commentCountsBatch(root);
  }
  /* comment counts on tiles (2026-08-10) — one batched get_comment_counts call per render
     batch; hidden at 0 so quiet tiles never advertise emptiness. async, never blocks render. */
  function commentCountsBatch(root) {
    var client = sbClient();
    if (!client) return;
    var pend = [];
    (root || document).querySelectorAll('.mfeed-tile[data-handle]').forEach(function (t) {
      var el = t.querySelector('[data-mfeed-ccount]');
      if (el && !el.getAttribute('data-cc-done')) { el.setAttribute('data-cc-done', '1'); pend.push({ h: t.getAttribute('data-handle'), el: el }); }
    });
    if (!pend.length) return;
    for (var i = 0; i < pend.length; i += 200) {
      (function (chunk) {
        try {
          client.rpc('get_comment_counts', { p_handles: chunk.map(function (x) { return x.h; }) }).then(function (r) {
            if (!r || r.error || !r.data) return;
            var map = {};
            r.data.forEach(function (row) { map[row.product_handle] = row.comment_count; });
            chunk.forEach(function (x) {
              var n = parseInt(map[x.h], 10) || 0;
              if (n > 0) { x.el.textContent = n + (n === 1 ? ' comment' : ' comments'); x.el.hidden = false; }
            });
          }, function () {});
        } catch (e) {}
      })(pend.slice(i, i + 200));
    }
  }
  /* "posted by" on community feed tiles — batch get_submission_attribution over every
     tile in `root` carrying a data-ref (submission uuid) that hasn't been attributed yet.
     the rpc returns approved/live submissions only, so pending posters never surface. anon-callable. */
  function attributeFeedBatch(root) {
    var client = sbClient();
    if (!client) return;
    var pending = [];
    (root || document).querySelectorAll('.mfeed-tile[data-ref]').forEach(function (t) {
      var by = t.querySelector('[data-mfeed-by]');
      if (by && !t.getAttribute('data-attr-done')) pending.push(t);
    });
    if (!pending.length) return;
    var ids = [];
    pending.forEach(function (t) { t.setAttribute('data-attr-done', '1'); ids.push(t.getAttribute('data-ref')); });
    for (var i = 0; i < ids.length; i += 200) {
      (function (chunk) {
        try {
          client.rpc('get_submission_attribution', { p_ids: chunk }).then(function (r) {
            if (!r || r.error || !r.data) return;
            var map = {};
            r.data.forEach(function (row) { map[row.submission_id] = row; });
            pending.forEach(function (t) {
              var row = map[t.getAttribute('data-ref')];
              if (!row) return;
              var by = t.querySelector('[data-mfeed-by]');
              if (!by) return;
              var nm = row.handle || row.display_name;
              if (!nm) return;
              by.textContent = 'by ' + nm;
              by.hidden = false;
            });
          }, function () {});
        } catch (e) {}
      })(ids.slice(i, i + 200));
    }
  }
  /* optimistic ±1 across every place this handle's count is shown (feed tiles + its tile page) */
  function bumpSaveCount(h, delta) {
    if (!h) return;
    document.querySelectorAll('.mfeed-tile[data-handle="' + h + '"] [data-mfeed-count]').forEach(function (el) {
      setFeedCount(el, (parseInt(el.getAttribute('data-count'), 10) || 0) + delta);
    });
    var mtp = document.querySelector('[data-mtp][data-handle="' + h + '"]');
    var tl = mtp ? mtp.querySelector('[data-mtp-likes]') : null;
    if (tl) setTileLikes(tl, (parseInt(tl.getAttribute('data-count'), 10) || 0) + delta);
  }

  /* ============================================================
     feed — infinite masonry over the section rendering api.
     [data-mfeed] attributes:
       data-total-pages / data-page (0 = nothing server-rendered)
       data-mfeed-src      fetch url (default /?section_id=mien-feed)
       data-mfeed-ordered  keep order, no shuffle, sequential pages
       data-mfeed-seed     integer seed for the start page
       data-mfeed-exclude  product handle to drop from batches
     ============================================================ */
  function feedInit() {
    var feedEl = document.querySelector('[data-mfeed]');
    if (!feedEl) return;
    var grid = feedEl.querySelector('[data-mfeed-grid]');
    var sentinel = feedEl.querySelector('[data-mfeed-sentinel]');
    var endEl = feedEl.querySelector('[data-mfeed-end]');
    if (!grid || !sentinel) return;
    var total = parseInt(feedEl.getAttribute('data-total-pages'), 10) || 1;
    var serverPage = parseInt(feedEl.getAttribute('data-page'), 10);
    if (isNaN(serverPage)) serverPage = 1;
    var src = feedEl.getAttribute('data-mfeed-src') || '/?section_id=mien-feed';
    var ordered = feedEl.hasAttribute('data-mfeed-ordered');
    var seed = parseInt(feedEl.getAttribute('data-mfeed-seed'), 10) || 0;
    var excl = feedEl.getAttribute('data-mfeed-exclude') || '';

    masonryInit(grid);
    syncFeedHearts(grid); /* reflect saved state on the server-rendered batch */
    /* the shuffled feed re-decides these a few lines down (noteReset + the instant paint);
       search / brand pages keep server order and this is the only pass they get. */
    noteReset();
    [].forEach.call(grid.querySelectorAll('.mfeed-tile'), function (t) { MienSignal.observe(t); detectCutout(t); noteApply(t); });
    countFeedBatch(grid); /* one get_tile_save_counts call for the server-rendered batch */
    attributeFeedBatch(grid); /* "posted by" on any community tiles in the server-rendered batch */

    var loading = false;
    var done = false;
    var io = null;

    function finish() {
      if (done) return;
      done = true;
      if (io) io.disconnect();
      if (endEl) endEl.hidden = false;
      if (window.FeedFilter) FeedFilter.refreshEmpty(); /* settle the "nothing here yet" state once exhausted */
    }

    /* ============================================================
       UNORDERED (the feed + keep-scrolling): global seeded arrangement (2026-08).
       every page is fetched up front, the FULL tile list is shuffled once with the
       session-seeded prng, brand runs of 3+ are broken up, and the grid then
       paginates CLIENT-side in chunks as the user scrolls. guarantees: stable order
       for the whole session (no dupes, no skips), brands interleave, and newly
       published pieces slot in anywhere the shuffle puts them — never at the end.
       the server-rendered batch shows instantly (seeded-shuffled) and is folded
       into the global arrangement the moment every page has arrived.
       ============================================================ */
    if (!ordered) {
      var rng = mulberry32(feedSessionSeed() ^ Math.imul(seed || 1, 2654435761));
      var sShuffle = function (a) {
        for (var i = a.length - 1; i > 0; i--) { var j = (rng() * (i + 1)) | 0, t = a[i]; a[i] = a[j]; a[j] = t; }
        return a;
      };
      var brandOf = function (t) {
        var b = t.querySelector('.mfeed-brand');
        return b ? b.textContent.trim().toLowerCase() : '';
      };
      /* proportional interleave — each brand's (already shuffled) tiles get seeded-jittered
         positions spread evenly over the whole list, so brands mix everywhere instead of
         clumping, whatever the brand ratio. jitter 0.35 keeps it feeling shuffled without
         letting big same-brand clumps form. */
      var interleave = function (list) {
        var groups = {};
        list.forEach(function (t) { var b = brandOf(t) || '·'; (groups[b] = groups[b] || []).push(t); });
        var keyed = [];
        Object.keys(groups).forEach(function (b) {
          var g = groups[b], n = g.length;
          g.forEach(function (t, i) { keyed.push({ t: t, k: (i + 0.325 + rng() * 0.35) / n }); });
        });
        keyed.sort(function (a, b) { return a.k - b.k; });
        return keyed.map(function (x) { return x.t; });
      };
      /* soft rule, tightened 2026-08-24: NO two adjacent same-brand tiles (was: no runs of
         3+). the offender swaps with the next tile of a different brand; the tail is left
         alone if none exists. the guard below still discards the pass when a lopsided brand
         ratio makes it counterproductive. */
      var fixRuns = function (list) {
        for (var i = 1; i < list.length; i++) {
          var b = brandOf(list[i]);
          if (!b || b !== brandOf(list[i - 1])) continue;
          var k = i + 1;
          while (k < list.length && brandOf(list[k]) === b) k++;
          if (k < list.length) { var t = list[i]; list[i] = list[k]; list[k] = t; }
        }
        return list;
      };
      var runStats = function (list) {
        var maxrun = 1, run = 1, v = 0;
        for (var i = 1; i < list.length; i++) {
          if (brandOf(list[i]) === brandOf(list[i - 1])) { run++; v++; }
          else run = 1;
          if (run > maxrun) maxrun = run;
        }
        return { maxrun: maxrun, v: v };
      };
      /* fixRuns is only KEPT when it genuinely improves the arrangement — with a lopsided
         brand ratio (e.g. 3:1) runs of 3+ are mathematically unavoidable and the greedy
         swap would drain the minority brand early, piling one brand at the end. the guard
         keeps the evenly-interleaved order in that case. */
      /* LANE DE-CLUMPING (2026-08-29). Brand interleaving alone does not stop the feed
         reading as "a block of outlet, then a block of full price" — the two lanes arrive
         in ingest batches, so a run of one lane can survive the brand pass intact. This
         breaks runs of 4+ of the same lane by pulling forward the nearest tile of the other
         lane; runs of 2 and 3 are left alone deliberately, because forcing alternation on a
         feed that is (say) 85% partner would drain the outlet pieces into the first screen
         and leave none below — the exact clumping the rule exists to prevent.
         Applied AFTER the brand pass and guarded the same way: kept only if it does not make
         same-brand adjacency worse. */
      var LANE_RUN = 4;
      var laneOf = function (t) { return t.getAttribute('data-lane') || 'partner'; };
      var fixLaneRuns = function (list) {
        var run = 1;
        for (var i = 1; i < list.length; i++) {
          if (laneOf(list[i]) !== laneOf(list[i - 1])) { run = 1; continue; }
          run++;
          if (run < LANE_RUN) continue;
          var lane = laneOf(list[i]);
          for (var k = i + 1; k < list.length; k++) {
            if (laneOf(list[k]) === lane) continue;
            var t = list[i]; list[i] = list[k]; list[k] = t;
            break;
          }
          run = (laneOf(list[i]) === laneOf(list[i - 1])) ? run : 1;
        }
        return list;
      };
      var arrange = function (list) {
        var base = interleave(list);
        var fixed = fixRuns(base.slice());
        var sb = runStats(base), sf = runStats(fixed);
        var best = (sf.maxrun < sb.maxrun || (sf.maxrun === sb.maxrun && sf.v <= sb.v)) ? fixed : base;
        var laned = fixLaneRuns(best.slice());
        var sl = runStats(laned), sbest = runStats(best);
        /* only keep the lane pass if it did not undo the brand pass's work */
        return (sl.maxrun <= sbest.maxrun && sl.v <= sbest.v + 1) ? laned : best;
      };
      /* stocked-first (2026-08-10) — pieces mien actually stocks and sells (the shopify
         collective lane) ride at the FRONT of the arrangement instead of wherever the shuffle
         drops them. this is a store merchandising its own inventory, not a third party buying
         placement, so it needs no "featured" badge — and the tile page says plainly that mien
         stocks and ships it. if a brand ever PAYS for placement, tag it placement:featured
         instead: that renders the badge in mien-feed-tile, and the two must ship together.
         applied last, after arrange + taste, so nothing downstream re-sorts them away.
         scattered, not pinned (2026-08-24): a solid block of stocked tiles at position 0
         reads as an ad slot. each one is dropped at a seeded-random slot inside the first
         few rows instead — reliably early, never the same exact lead tiles twice. the
         session-seeded rng keeps the placement stable within a session. */
      /* 2026-08-24: the collective lane LEFT this scatter — natives now get deterministic
         guaranteed slots (position 2, then every Nth; see the position floor in drawMore).
         this hoist still serves placement:featured, the paid-placement lane. */
      var hoistFeatured = function (list) {
        var top = [], rest = [];
        list.forEach(function (t) {
          var tags = t.getAttribute('data-tags') || '';
          if (tags.indexOf('placement:featured') >= 0) top.push(t);
          else rest.push(t);
        });
        if (!top.length) return list;
        /* one stocked tile is guaranteed 2nd–4th — reliably early without the giveaway
           of always leading — and the rest scatter close behind it (2026-08-24) */
        var lead = top.shift();
        var span = Math.min(rest.length, Math.max(10, top.length * 3));
        top.forEach(function (t) {
          rest.splice(2 + Math.floor(rng() * span), 0, t);
        });
        rest.splice(1 + Math.floor(rng() * 3), 0, lead);
        return rest;
      };

      /* instant paint: the server batch KEEPS its order while the rest loads (2026-08-24).
         it used to be seeded-shuffled here because the server served raw collection order —
         but mien-feed.liquid now emits a vendor round-robin (daily-rotated), and shuffling
         that would put same-brand tiles back next to each other for the first seconds. */
      noteReset();
      [].slice.call(grid.children).forEach(function (t) { noteApply(t); });

      var CHUNK = 24;
      var pool = null;   /* the full, globally-ordered tile list (detached nodes) */
      var drawn = 0;

      /* ============================================================
         NATIVE POSITION FLOOR (2026-08-24) — native/collective tiles get guaranteed
         impressions at every scroll depth: rendered position 2, then every Nth
         (data-mfeed-native-int, a theme setting, default 8). in-stock natives are
         LIFTED OUT of the regular pool and dealt back at their slots, cycling; a
         native repeats across draw chunks (each chunk ≈ one page) but never twice
         WITHIN one — repeat appearances are clones marked data-mfeed-echo, which
         every filtered view hides so filters never show duplicates. sold-out
         natives stay in the normal flow with their badge. degenerate guard: if
         natives exceed 25% of the pool, injection stands down entirely and the
         round-robin arrangement carries them like any other tile.
         ============================================================ */
      var NAT_INT = parseInt(feedEl.getAttribute('data-mfeed-native-int'), 10) || 8;
      if (NAT_INT < 2) NAT_INT = 2;
      var natives = [];       /* in-stock native tiles, in pool order */
      var natInject = false;
      var natPtr = 0;         /* cycle pointer */
      var natShown = {};      /* handle -> times dealt (first = real node, later = echo clones) */
      var gPos = 0;           /* tiles appended to the grid since the pool rebuild */
      var natSlot = 1;        /* next slot, 0-based (1 = the 2nd tile) */
      function nextNative(usedThisChunk) {
        for (var i = 0; i < natives.length; i++) {
          var cand = natives[(natPtr + i) % natives.length];
          var h = cand.getAttribute('data-handle');
          if (usedThisChunk[h]) continue; /* never the same product twice in one chunk */
          natPtr = (natPtr + i + 1) % natives.length;
          usedThisChunk[h] = true;
          var n = natShown[h] || 0;
          natShown[h] = n + 1;
          if (!n) return cand;
          var clone = cand.cloneNode(true);
          clone.setAttribute('data-mfeed-echo', '1');
          return clone;
        }
        return null; /* all natives already in this chunk — the slot takes a regular tile */
      }

      var fetchPage = function (page, attempt) {
        return fetch(src + '&page=' + page)
          .then(function (r) { if (!r.ok) throw new Error('page ' + page); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            return [].slice.call(doc.querySelectorAll('.mfeed-tile')).map(function (t) {
              return document.importNode(t, true);
            });
          })
          .catch(function (e) {
            if ((attempt || 0) < 2) return fetchPage(page, (attempt || 0) + 1);
            try { console.warn('[mien] feed page failed', page, e); } catch (e2) {}
            return []; /* a dead page never blocks the rest */
          });
      };

      /* ------------------------------------------------------------
         page fetch — DEFERRED AND THROTTLED (2026-08-31).
         this used to fire all 56 page requests the instant the script ran, which put the
         whole rest of the catalogue in front of the visible tiles' own images in the
         browser's request queue: the first screen sat half-drawn for seconds while a
         catalogue nobody had scrolled to yet came down the wire. the pool is not needed
         until the reader reaches the bottom of the server-rendered batch, so the burst
         waits for `load` (the first screen has its images by then) and runs six at a
         time instead of all at once. wall-clock to a full pool is about the same —
         the connection was the ceiling, not the concurrency.
         ------------------------------------------------------------ */
      var FETCH_CONC = 6;
      function fetchAllPages() {
        var queue = [];
        for (var p = 1; p <= total; p++) { if (p !== serverPage) queue.push(p); }
        var out = [];
        function worker() {
          if (!queue.length) return Promise.resolve();
          var page = queue.shift();
          return fetchPage(page).then(function (tiles) {
            out.push(tiles);
            return worker();
          });
        }
        var lanes = [];
        for (var i = 0; i < Math.min(FETCH_CONC, Math.max(queue.length, 1)); i++) lanes.push(worker());
        return Promise.all(lanes).then(function () { return out; });
      }

      function buildPool(batches) {
        /* WHAT IS ALREADY ON SCREEN STAYS ON SCREEN (2026-08-31).
           this block used to empty the grid and redraw it from the global arrangement the
           moment the last page landed — a second or two after load, every tile the reader
           was looking at vanished and was replaced by different ones. it read as the page
           spontaneously refreshing itself, and it threw away scroll position with it.
           now the server-rendered batch is PINNED at the front of the pool and the global
           arrangement is applied to the remainder, which is appended behind it as the
           reader scrolls. same guarantees as before (no dupes, no skips, brands interleave,
           stable for the whole session) with nothing repainting under the reader. */
        var kept = [].slice.call(grid.querySelectorAll('.mfeed-tile'));
        var rest = [];
        batches.forEach(function (b) { rest = rest.concat(b); });
        /* safety net: one tile per handle, current product excluded on keep-scrolling.
           PLUS one tile per vendor+title (2026-08-24): duplicate ingests ship as
           handle / handle-1 pairs (shooting-stars-cap), and the pool is where the whole
           feed converges, so the second copy dies here for every page at once.
           the kept tiles SEED both maps: nothing already painted can come back below. */
        var seen = {}, seenVT = {};
        var vtKey = function (t) {
          var h = t.getAttribute('data-handle') || '';
          var im = t.querySelector('.mfeed-plate > img');
          return (t.getAttribute('data-brand') || '') + '~' + ((im && im.getAttribute('alt')) || h).toLowerCase();
        };
        kept.forEach(function (t) {
          var h = t.getAttribute('data-handle');
          if (h) { seen[h] = true; seenVT[vtKey(t)] = true; }
        });
        rest = rest.filter(function (t) {
          var h = t.getAttribute('data-handle');
          if (!h || seen[h] || h === excl) return false;
          var vt = vtKey(t);
          if (seenVT[vt]) return false;
          seen[h] = true; seenVT[vt] = true; return true;
        });
        /* taste pass (2026-08-10): after the seeded arrange, bias toward the member's
           style profile (this-or-that picks + saved hearts). pure reorder — same tiles. */
        rest = hoistFeatured(MienTaste.rank(arrange(sShuffle(rest)), rng));
        /* taste-rank + the stocked-tile scatter both run after arrange, and either can put
           two same-brand tiles back together — one last guarded de-adjacency pass ends it. */
        (function () {
          var fin = fixRuns(rest.slice());
          var sp = runStats(rest), sf = runStats(fin);
          if (sf.maxrun < sp.maxrun || (sf.maxrun === sp.maxrun && sf.v <= sp.v)) rest = fin;
        })();
        /* position floor: lift the in-stock natives out of the REMAINDER; they deal back
           in at their slots as it draws. the kept tiles already carry the server-rendered
           native slots (mien-feed.liquid runs the same floor), so measuring the 25% guard
           over the remainder measures it over exactly what injection will apply to. */
        natives = rest.filter(nativeInStock);
        natInject = natives.length > 0 && natives.length * 4 <= rest.length;
        if (natInject) rest = rest.filter(function (t) { return !nativeInStock(t); });
        else natives = [];
        pool = kept.concat(rest);
        drawn = kept.length;
        /* the slot counter continues from what is on screen rather than restarting, so the
           native cadence carries across the seam instead of doubling up at the join. */
        gPos = kept.length;
        natSlot = 1;
        while (natSlot < gPos) natSlot += NAT_INT;
        natPtr = 0; natShown = {};
        /* the this-or-that onboarding samples its pairs from here */
        window.MienFeedPool = function () { return pool ? pool.slice() : null; };
        try { document.dispatchEvent(new CustomEvent('mien:feedpool')); } catch (e) {}
        /* nothing is removed and nothing repaints — drawMore only ever appends from `drawn`
           onward. maybeMoreDraw, not drawMore: if the reader has not reached the bottom of
           the server batch yet there is nothing to add, and the observer handles the rest.
           it also covers the case where the sentinel was already in view while the pool was
           still null (drawMore no-ops then, and the observer would not fire a second time). */
        maybeMoreDraw();
      }

      /* wait for the first screen to settle before pulling the catalogue down. the timer is
         a floor, not a delay on top of load: whichever comes first wins, so a page that
         never fires load (a stalled third-party asset) still fills its pool.
         ONLY when there is a server-rendered batch to protect. keep-scrolling on a tile page
         and "keep looking" under the cart both start from an EMPTY grid (data-page="0") —
         nothing is competing for bandwidth there and the section has nothing to show until
         the pool lands, so those fetch straight away as they always did. */
      var poolStarted = false;
      var startPool = function () {
        if (poolStarted) return;
        poolStarted = true;
        fetchAllPages().then(buildPool);
      };
      if (!grid.querySelector('.mfeed-tile') || document.readyState === 'complete') startPool();
      else {
        window.addEventListener('load', startPool, { once: true });
        setTimeout(startPool, 2500);
      }

      /* onboarding just finished → re-rank only the UNDRAWN remainder, so what's on
         screen never jumps but everything still to come is already personalized */
      document.addEventListener('mien:tastechange', function () {
        if (!pool || done) return;
        var rest = pool.slice(drawn);
        if (rest.length > 1) pool = pool.slice(0, drawn).concat(MienTaste.rank(rest, rng));
      });

      var drawMore = function () {
        if (!pool || done) return;
        /* assemble one chunk (≈ one page): regular pool tiles, with a native dealt in
           whenever the running position hits a slot. a slot with no native left for this
           chunk just takes the next regular tile — never a gap, never a repeat in-chunk. */
        var slice = [];
        var usedThisChunk = {};
        while (slice.length < CHUNK && (drawn < pool.length)) {
          if (natInject && gPos === natSlot) {
            natSlot += NAT_INT;
            var nat = nextNative(usedThisChunk);
            if (nat) { slice.push(nat); gPos++; continue; }
          }
          slice.push(pool[drawn++]); gPos++;
        }
        slice.forEach(function (t) {
          grid.appendChild(t);
          noteApply(t); /* before layoutTile — a revealed note is height the span has to hold */
          layoutTile(t);
          FeedFilter.applyTile(t);
          syncFeedHearts(t);
          MienSignal.observe(t);
          detectCutout(t);
        });
        FeedFilter.refreshEmpty();
        countFeedBatch(grid);
        attributeFeedBatch(grid);
        if (drawn >= pool.length) { finish(); return; }
        requestAnimationFrame(maybeMoreDraw);
      };
      var maybeMoreDraw = function () {
        if (done || !pool) return;
        var r = sentinel.getBoundingClientRect();
        if (r.top < window.innerHeight + 900) drawMore();
      };

      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting && !done) drawMore(); });
      }, { rootMargin: '900px 0px' });
      io.observe(sentinel);
      return;
    }

    /* ============================================================
       ORDERED (search results): sequential pages, relevance order — unchanged.
       ============================================================ */
    var consumed = {};
    if (serverPage >= 1) consumed[serverPage] = true;
    var fails = {};
    var next = (Math.max(serverPage, 1) % total) + 1;

    function pagesLeft() { return Object.keys(consumed).length < total; }

    var SKEL_RATIOS = ['3/4', '4/5', '1/1', '4/5', '3/4', '5/4', '1/1', '4/3']; /* the snapped set, portrait-lean */
    var skels = [];
    function showSkeletons() {
      SKEL_RATIOS.forEach(function (r) {
        var s = document.createElement('div');
        s.className = 'mfeed-skel';
        s.style.aspectRatio = r;
        s.setAttribute('aria-hidden', 'true');
        grid.appendChild(s);
        layoutTile(s);
        skels.push(s);
      });
    }
    function clearSkeletons() {
      skels.forEach(function (s) { s.remove(); });
      skels = [];
    }

    function maybeMore() {
      if (done || loading) return;
      var r = sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight + 900) loadNext();
    }

    function loadNext() {
      while (consumed[next] && pagesLeft()) next = (next % total) + 1;
      if (!pagesLeft()) { finish(); return; }
      loading = true;
      var page = next;
      consumed[page] = true;
      showSkeletons();
      fetch(src + '&page=' + page)
        .then(function (r) { if (!r.ok) throw new Error('fetch failed'); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var tiles = [].slice.call(doc.querySelectorAll('.mfeed-tile'));
          if (excl) tiles = tiles.filter(function (t) { return t.getAttribute('data-handle') !== excl; });
          if (!ordered) tiles = shuffle(tiles);
          clearSkeletons();
          tiles.forEach(function (t) {
            var node = document.importNode(t, true);
            grid.appendChild(node);
            noteApply(node); /* before layoutTile — a revealed note is height the span has to hold */
            layoutTile(node);
            FeedFilter.applyTile(node); /* honor the active category on freshly loaded batches */
            syncFeedHearts(node); /* new batch reflects current saved state */
            MienSignal.observe(node); /* count an impression once it's dwelled on-screen */
            detectCutout(node); /* float white-bg / cutout shots instead of full-bleed */
          });
          FeedFilter.refreshEmpty(); /* a filtered batch may have (un)filled the grid */
          countFeedBatch(grid); /* counts for the freshly appended batch (only new handles) */
          attributeFeedBatch(grid); /* "posted by" for the freshly appended community tiles */
          loading = false;
          if (!pagesLeft()) finish();
          else requestAnimationFrame(maybeMore);
        })
        .catch(function () {
          clearSkeletons();
          fails[page] = (fails[page] || 0) + 1;
          if (fails[page] < 3) delete consumed[page];
          loading = false;
          if (!pagesLeft()) finish();
        });
    }

    var io = null;
    if (!pagesLeft()) { finish(); return; }
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting && !loading && !done) loadNext(); });
    }, { rootMargin: '900px 0px' });
    io.observe(sentinel);
    requestAnimationFrame(maybeMore); /* pages with no server batch start immediately */
  }

  /* ============================================================
     OUTLET GATE — REMOVED 2026-08-31.

     Between 2026-08-29 and 2026-08-31 a lane:outlet piece rendered no price to a
     signed-out visitor: the feed tile shipped a "sign in for price" button, the
     product page shipped a gated slot with variants priced null, and this module
     asked the outlet-price edge function for the numbers once a member session was
     confirmed. The sign-in requirement is gone — every price is server-rendered for
     everyone now, so there is nothing left to reveal and nothing left to wire.

     WHAT THIS DID NOT TOUCH, and must stay: outlet PRODUCT PAGES are still
     `noindex` (layout/theme.liquid), still seo.hidden so they stay out of
     sitemap.xml, still held out of every product/shopping feed, and
     templates/robots.txt.liquid still disallows /products/*.js|json. Only the
     visitor-facing gate was dropped, never the crawler-facing one.

     MIEN.outletPrices() in mien-supabase.js and the outlet-price edge function are
     both left in place but now have no caller in the theme.
     ============================================================ */

  /* ============================================================
     tile page — variant pills, ajax add-to-cart, closet save.
     ============================================================ */
  function tilePageInit() {
    var root = document.querySelector('[data-mtp]');
    if (!root) return;
    var SYM = root.getAttribute('data-currency') || '$';
    var variants = [];
    try { variants = (JSON.parse(document.getElementById('mtp-data').textContent).variants || []); } catch (e) {}
    /* every variant now arrives with a real price on every lane (the outlet gate that used
       to send price:null and patch this array in place was removed 2026-08-31). Still parked
       on the element rather than kept private — harmless, and other code reads it. */
    root._mtpVariants = variants;

    /* public save count for THIS product — always shown, including 0 */
    var pHandle = root.getAttribute('data-handle');
    var likesEl = root.querySelector('[data-mtp-likes]');
    if (pHandle && likesEl) {
      fetchSaveCounts([pHandle], function (map) { if (pHandle in map) setTileLikes(likesEl, map[pHandle]); });
    }
    /* community attribution — get_submission_attribution(source_ref uuid) → one tiny "posted by …" line.
       the rpc returns approved/live submissions only, so pending posters never surface. anon-callable. */
    var attrEl = root.querySelector('[data-mtp-attr]');
    var ref = root.getAttribute('data-mtp-community-ref');
    if (attrEl && ref && sbClient()) {
      try {
        sbClient().rpc('get_submission_attribution', { p_ids: [ref] }).then(function (r) {
          if (!r || r.error || !r.data || !r.data.length) return;
          var row = null;
          for (var i = 0; i < r.data.length; i++) { if (r.data[i].submission_id === ref) { row = r.data[i]; break; } }
          if (!row) return;
          var name = row.handle || row.display_name;
          if (!name) return;
          attrEl.textContent = 'posted by ' + name;
          attrEl.hidden = false;
        }, function () {});
      } catch (e) {}
    }

    function findVariant(vid) {
      for (var i = 0; i < variants.length; i++) { if (variants[i].id === vid) return variants[i]; }
      return null;
    }

    root.addEventListener('click', function (e) {
      var val = e.target.closest('.mtp-val');
      if (val && !val.disabled) {
        var v = findVariant(parseInt(val.getAttribute('data-vid'), 10));
        if (!v) return;
        root.querySelectorAll('.mtp-val').forEach(function (b) {
          var on = b === val;
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        /* keep BOTH the main block and the sticky bar in sync. the typeof guard stays: a
           variant with no numeric price must not print "$NaN". */
        root.querySelectorAll('[data-mtp-price]').forEach(function (el) {
          if (typeof v.price !== 'number') return;
          el.textContent = money(v.price, SYM);
        });
        root.querySelectorAll('.mtp-atc').forEach(function (atc) {
          atc.setAttribute('data-vid', v.id);
          atc.disabled = !v.available;
          atc.textContent = v.available ? 'add to cart' : 'sold out';
        });
        /* truthful low-stock line follows the selected variant (inv = -1 when untracked) */
        var scarce = root.querySelector('[data-mtp-scarce]');
        if (scarce) {
          var inv = (typeof v.inv === 'number') ? v.inv : -1;
          if (v.available && inv >= 1 && inv <= 3) { scarce.textContent = 'only ' + inv + ' left'; scarce.hidden = false; }
          else scarce.hidden = true;
        }
        return;
      }

      var atcBtn = e.target.closest('.mtp-atc');
      if (atcBtn) {
        var vid = parseInt(atcBtn.getAttribute('data-vid'), 10);
        if (!vid || atcBtn.disabled || atcBtn.classList.contains('is-busy')) return;
        atcBtn.classList.add('is-busy');
        /* the theme's existing ajax cart — /cart/*.js + header count, no new flow */
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1 })
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function () {
            atcBtn.classList.remove('is-busy');
            atcBtn.classList.add('is-added');
            atcBtn.textContent = 'added';
            updateCartCount();
            MienSignal.log('cart_add', root.getAttribute('data-handle'));
            setTimeout(function () {
              atcBtn.classList.remove('is-added');
              atcBtn.textContent = 'add to cart';
            }, 2200);
          })
          .catch(function () {
            atcBtn.classList.remove('is-busy');
            atcBtn.textContent = 'couldn’t add — try again';
            setTimeout(function () { atcBtn.textContent = 'add to cart'; }, 2200);
          });
      }
    });

    /* social proof: "in N closets" when N >= 5 — public aggregate via the
       closet_count rpc (security-definer; RLS on the table is untouched).
       silent on error / below threshold. */
    var social = root.querySelector('[data-mtp-social]');
    var handle = root.getAttribute('data-handle');
    if (social && handle && window.MIEN && MIEN.ready && MIEN.ready() && MIEN.closetCount) {
      MIEN.closetCount(handle).then(function (n) {
        if (n >= 5) { social.textContent = 'in ' + n + ' closets'; social.hidden = false; }
      }).catch(function () {});
    }

    /* sticky buy bar (mobile): reveal once the action block has scrolled above the top */
    var action = root.querySelector('.mtp-action');
    var sticky = root.querySelector('[data-mtp-sticky]');
    if (action && sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var past = en.boundingClientRect.bottom < 64; /* scrolled up past the header zone */
          sticky.classList.toggle('is-show', !en.isIntersecting && past);
        });
      }, { threshold: 0 }).observe(action);
    }

    syncSaveButtons();

    /* dwell — how long this tile page stayed open; sent once on first hide/unload */
    var dwellStart = Date.now();
    var dwellSent = false;
    function endDwell() {
      if (dwellSent) return;
      dwellSent = true;
      MienSignal.log('dwell', root.getAttribute('data-handle'), { duration_ms: Date.now() - dwellStart });
      MienSignal.flush(true);
    }
    window.addEventListener('pagehide', endDwell);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') endDwell();
    });
  }

  /* ============================================================
     closet — saves tab renders closet handles as tile links with
     remove + "now available" marks; purchases tab and the profile
     bit are wired in the section (liquid) + here.
     ============================================================ */
  function closetInit() {
    var rootEl = document.querySelector('[data-mcloset]');
    if (!rootEl) return;
    var grid = rootEl.querySelector('[data-mcloset-grid]');
    var countEl = rootEl.querySelector('[data-mcloset-count]');
    var emptyEl = rootEl.querySelector('[data-mcloset-empty]');
    var keepEl = rootEl.querySelector('[data-mcloset-keep]');
    var profEl = rootEl.querySelector('[data-mcl-prof]');
    if (!grid) return;
    masonryInit(grid);

    /* signed-out "keep your closet" → the reusable signup modal (not a dead /pages link).
       on success the closet auto-switches to account mode via the mien:closet event below. */
    if (keepEl) {
      var keepBtn = keepEl.querySelector('[data-mcl-keep-open]');
      if (keepBtn) keepBtn.addEventListener('click', function () {
        if (window.MienAuthModal && MienAuthModal.open) MienAuthModal.open({ mode: 'create', source: 'save' });
      });
    }

    function sync(n, mode) {
      if (countEl) countEl.textContent = n; /* just the number — the label lives in the stat line */
      if (emptyEl) emptyEl.hidden = n > 0;
      if (keepEl) keepEl.hidden = (mode || MienCloset.mode()) === 'account';
    }

    function tileFor(p) {
      var src = (p.images && p.images[0]) || p.featured_image || '';
      var wrap = document.createElement('div');
      wrap.className = 'mcl-item';
      wrap.style.aspectRatio = '4 / 5'; /* provisional plate shape; snapped once the image loads */
      var a = document.createElement('a');
      a.className = 'mfeed-tile is-plate'; /* saved pieces are product shots — contain on the plate */
      a.href = '/products/' + p.handle;
      a.setAttribute('data-handle', p.handle);
      a.setAttribute('aria-label', lc(p.title) + (p.vendor ? ' — ' + lc(p.vendor) : ''));
      var im = document.createElement('img');
      im.src = imgUrl(src, 480);
      im.setAttribute('srcset', imgUrl(src, 300) + ' 300w, ' + imgUrl(src, 480) + ' 480w, ' + imgUrl(src, 700) + ' 700w');
      im.setAttribute('sizes', '(min-width: 1024px) 24vw, (min-width: 700px) 32vw, 48vw');
      im.loading = 'lazy';
      im.alt = lc(p.title);
      im.addEventListener('load', function () {
        if (im.naturalWidth && im.naturalHeight) {
          wrap.style.aspectRatio = String(snapRatio(im.naturalWidth / im.naturalHeight));
          layoutTile(wrap);
        }
      }, { once: true });
      a.appendChild(im);
      /* featured when saved, buyable now → quiet "now available" mark */
      var savedTier = TierCache.get(p.handle);
      if (!savedTier) TierCache.set(p.handle, productBuyable(p) ? 'buyable' : 'featured');
      if (savedTier === 'featured' && productBuyable(p)) {
        var dot = document.createElement('span');
        dot.className = 'mcl-dot';
        dot.innerHTML = '<i aria-hidden="true"></i>now available';
        a.appendChild(dot);
      }
      var rm = document.createElement('button');
      rm.className = 'mcl-remove';
      rm.type = 'button';
      rm.setAttribute('data-handle', p.handle);
      rm.setAttribute('aria-label', 'remove ' + lc(p.title) + ' from saves');
      rm.innerHTML = '&times;';
      wrap.appendChild(a);
      wrap.appendChild(rm);
      return wrap;
    }

    /* render a saved piece ENTIRELY from its snapshot (title/brand/price/image_url) so it survives the
       Shopify product being deleted outright. `gone` → dimmed + "no longer available" mark. `linkable`
       keeps it a real link to the archived tile page (dead products stay published); a confirmed 404
       passes linkable=false → non-link so we never send anyone to a dead URL. transient miss (gone=false)
       → normal linked tile, no label, so we never falsely claim a live piece is gone. */
    function snapTileFor(h, snap, gone, linkable) {
      snap = snap || {};
      var asLink = (!gone) || !!linkable;
      var wrap = document.createElement('div');
      wrap.className = 'mcl-item' + (gone ? ' is-gone' : '');
      wrap.style.aspectRatio = '4 / 5';
      var fig = asLink ? document.createElement('a') : document.createElement('div');
      fig.className = 'mfeed-tile is-plate';
      if (asLink) { fig.href = '/products/' + h; }
      fig.setAttribute('data-handle', h);
      if (snap.image_url) {
        var im = document.createElement('img');
        im.src = snap.image_url; im.loading = 'lazy'; im.alt = snap.title || '';
        im.addEventListener('load', function () {
          if (im.naturalWidth && im.naturalHeight) {
            wrap.style.aspectRatio = String(snapRatio(im.naturalWidth / im.naturalHeight));
            layoutTile(wrap);
          }
        }, { once: true });
        fig.appendChild(im);
      }
      if (gone) {
        var lbl = document.createElement('span');
        lbl.className = 'mcl-gone';
        lbl.textContent = 'no longer available';
        fig.appendChild(lbl);
      }
      var rm = document.createElement('button');
      rm.className = 'mcl-remove'; rm.type = 'button';
      rm.setAttribute('data-handle', h);
      rm.setAttribute('aria-label', 'remove ' + lc(snap.title || h) + ' from saves');
      rm.innerHTML = '&times;';
      wrap.appendChild(fig); wrap.appendChild(rm);
      return wrap;
    }

    /* persist product_gone=true for this handle across ALL holders (security-definer RPC), once. */
    var _goneMarked = {};
    function markGone(h) {
      if (!h || _goneMarked[h]) return;
      _goneMarked[h] = true;
      try { if (window.MIEN && MIEN.markClosetGone) MIEN.markClosetGone(h); } catch (e) {}
    }

    function appendHandle(h) {
      /* dead (server truth = window.MIEN_DEAD, tag state:dead) OR already-persisted product_gone →
         render as GONE straight from the snapshot, no fetch. dead products stay published so the tile
         stays clickable to the archived page (linkable); a product_gone with no MIEN_DEAD membership is
         a deleted product → non-link. persist product_gone so it survives the product being deleted
         (which drops it out of collections.dead entirely). */
      var isDead = (window.MIEN_DEAD || []).indexOf(h) > -1;
      var snapNow = MienCloset.snap(h);
      if (isDead || (snapNow && snapNow.product_gone)) {
        if (snapNow && !grid.querySelector('.mfeed-tile[data-handle="' + h + '"]')) {
          var d = snapTileFor(h, snapNow, true, isDead); /* linkable only while still published (dead) */
          grid.appendChild(d);
          layoutTile(d);
        }
        if (isDead) markGone(h); /* persist for when the product is later deleted */
        return Promise.resolve();
      }
      return fetchProduct(h).then(function (p) {
        if (p && !p.gone) {                              /* verified live → normal tile, no label */
          if (!grid.querySelector('.mfeed-tile[data-handle="' + h + '"]')) {
            var t = tileFor(p);
            grid.appendChild(t);
            layoutTile(t);
          }
          return;
        }
        /* only p.gone is a CONFIRMED 404 (unpublished/removed); p===null is a transient
           fetch failure — never label that as gone. fall back to the saved snapshot. */
        var confirmedGone = !!(p && p.gone);
        var snap = MienCloset.snap(h);
        if (!snap) { if (confirmedGone) MienCloset.remove(h); return; }
        if (!grid.querySelector('.mfeed-tile[data-handle="' + h + '"]')) {
          var g = snapTileFor(h, snap, confirmedGone);
          grid.appendChild(g);
          layoutTile(g);
        }
      });
    }

    var handles = MienCloset.get();
    sync(handles.length);
    handles.forEach(appendHandle);

    grid.addEventListener('click', function (e) {
      var rm = e.target.closest('.mcl-remove');
      if (rm) { e.preventDefault(); MienCloset.remove(rm.getAttribute('data-handle')); }
    });

    document.addEventListener('mien:closet', function (e) {
      var hs = e.detail.handles;
      sync(hs.length, e.detail.mode);
      [].slice.call(grid.querySelectorAll('.mcl-item')).forEach(function (item) {
        var b = item.querySelector('.mfeed-tile');
        if (hs.indexOf(b.getAttribute('data-handle')) === -1) item.remove();
      });
      hs.forEach(function (h) {
        if (!grid.querySelector('.mfeed-tile[data-handle="' + h + '"]')) appendHandle(h);
      });
    });

    /* tabs: saves / purchases */
    rootEl.querySelectorAll('[data-mcl-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var which = tab.getAttribute('data-mcl-tab');
        rootEl.querySelectorAll('[data-mcl-tab]').forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        rootEl.querySelectorAll('[data-mcl-pane]').forEach(function (p) {
          p.hidden = p.getAttribute('data-mcl-pane') !== which;
        });
        document.querySelectorAll('.mfeed-grid.is-masonry').forEach(layoutGrid);
      });
    });

    /* ---- header identity: avatar · editable name · one dateline. same supabase session as
       community, no second auth. member NUMBER stays unsurfaced (no count signal). ---- */
    var avBtn = rootEl.querySelector('[data-mcl-avatar]');
    var avImg = rootEl.querySelector('[data-mcl-avatar-img]');
    var avPh = rootEl.querySelector('[data-mcl-avatar-ph]');
    var avInput = rootEl.querySelector('[data-mcl-avatar-input]');
    var nameEl = rootEl.querySelector('[data-mcl-name]');
    var nameInput = rootEl.querySelector('[data-mcl-name-input]');
    var nameEdit = rootEl.querySelector('[data-mcl-name-edit]');
    var nameHint = rootEl.querySelector('[data-mcl-name-hint]');
    var joinedEl = rootEl.querySelector('[data-mcl-joined]');
    var quietEl = rootEl.querySelector('[data-mcl-quiet]'); /* replay intro · sign out */
    var signedInNow = false;

    function monthYear(iso) {
      try { var d = new Date(iso); if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toLowerCase();
      } catch (e) { return ''; }
    }
    function paintAvatar(url, name) {
      if (url && avImg) { avImg.src = url; avImg.hidden = false; if (avPh) avPh.hidden = true; }
      else { if (avImg) avImg.hidden = true; if (avPh) { avPh.hidden = false; avPh.textContent = ((name || 'm').trim().charAt(0) || 'm').toLowerCase(); } }
    }
    function hideIdentity() {
      if (profEl) profEl.hidden = true;
      if (nameEdit) nameEdit.hidden = true;
      if (quietEl) quietEl.hidden = true;
      if (joinedEl) joinedEl.hidden = true;
      if (avBtn) avBtn.classList.remove('can-edit');
      if (nameEl) nameEl.textContent = 'Your Account';
      paintAvatar(null, 'm');
      signedInNow = false;
    }
    function paintProfile(pr, email) {
      var name = (pr && (pr.display_name || pr.handle)) || (email ? email.split('@')[0] : 'your closet');
      if (nameEl) nameEl.textContent = name;
      /* the ONE dateline — lowercase "joined mon yyyy", nowhere else */
      if (joinedEl) { var jy = pr && pr.created_at ? monthYear(pr.created_at) : ''; if (jy) { joinedEl.textContent = 'joined ' + jy; joinedEl.hidden = false; } else joinedEl.hidden = true; }
      paintAvatar(pr && pr.avatar_url, name);
    }

    /* ---- lists (shopping-style grid) + posts (analytics + click counts): both from
       get_my_posted_tiles, one fetch, authed-only ---- */
    var listsGrid = rootEl.querySelector('[data-mcl-lists-grid]');
    var listsEmpty = rootEl.querySelector('[data-mcl-lists-empty]');
    var postedGrid = rootEl.querySelector('[data-mcl-posted-grid]');
    var postedEmpty = rootEl.querySelector('[data-mcl-posted-empty]');

    /* ---- mien credit: balance + history (get_my_credit_balance) + per-post earned map ---- */
    var creditWrap = rootEl.querySelector('[data-mcl-credit]');
    var creditAmt = rootEl.querySelector('[data-mcl-credit-amt]');
    var creditPendingEl = rootEl.querySelector('[data-mcl-credit-pending]');
    var creditHist = rootEl.querySelector('[data-mcl-credit-hist]');
    var creditConfirmed = {}; /* submission_id → confirmed cents */
    var creditPending = {};   /* submission_id → pending cents */
    function fmtMoney(cents) { var v = (Number(cents) || 0) / 100; return '$' + v.toFixed(2); }
    /* ---- balance ticker: count up 0→balance on load, tick old→new on change, green pulse on settle.
       cents roll as it counts (fmtMoney every frame). transform/opacity + text only. reduced-motion → snap. */
    var lastCreditCents = null; var creditRaf = 0;
    var creditStill = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    function pulseCredit() {
      if (!creditAmt || creditStill) return;
      creditAmt.classList.remove('is-ticked');
      void creditAmt.offsetWidth; /* restart the keyframe */
      creditAmt.classList.add('is-ticked');
      setTimeout(function () { if (creditAmt) creditAmt.classList.remove('is-ticked'); }, 700);
    }
    function tickCredit(toCents) {
      toCents = Number(toCents) || 0;
      if (!creditAmt) { lastCreditCents = toCents; return; }
      var prev = lastCreditCents, from = (prev == null) ? 0 : prev, first = (prev == null);
      var grew = (prev != null && toCents > prev);
      lastCreditCents = toCents;
      if (creditStill || from === toCents) { creditAmt.textContent = fmtMoney(toCents); if (grew) pulseCredit(); return; }
      if (creditRaf) cancelAnimationFrame(creditRaf);
      var dur = first ? 800 : 560, t0 = 0;
      function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur), e = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
        creditAmt.textContent = fmtMoney(Math.round(from + (toCents - from) * e));
        if (p < 1) { creditRaf = requestAnimationFrame(step); }
        else { creditRaf = 0; creditAmt.textContent = fmtMoney(toCents); if (grew) pulseCredit(); }
      }
      creditRaf = requestAnimationFrame(step);
    }
    function creditReason(h) {
      if (h.reason === 'link_revenue') return 'sales earnings';
      if (h.reason === 'post_approved') {
        if (h.submission_type === 'fitpic') return 'fit approved';
        if (h.submission_type === 'affiliate') return 'find approved';
        return 'post approved';
      }
      if (h.reason === 'finders_fee') return "finder's fee";
      return h.reason || 'credit';
    }
    function creditExpiry(h) {
      var t = new Date(h.expires_at).getTime(); if (isNaN(t)) return '';
      var d = Math.ceil((t - Date.now()) / 86400000);
      return d <= 0 ? 'expired' : ('expires in ' + d + 'd');
    }
    function creditTrail(h) { return h.status === 'pending' ? 'pending' : creditExpiry(h); }
    function renderCreditHist(rows) {
      if (!creditHist) return;
      creditHist.innerHTML = '';
      rows.slice(0, 12).forEach(function (h) {
        var row = document.createElement('div'); row.className = 'mcl-credit-row' + (h.status === 'pending' ? ' is-pending' : '');
        var what = document.createElement('span'); what.className = 'mcl-credit-what';
        what.textContent = creditReason(h) + ' +' + fmtMoney(h.amount_cents);
        var trail = document.createElement('span'); trail.className = 'mcl-credit-exp'; trail.textContent = creditTrail(h);
        row.appendChild(what); row.appendChild(trail); creditHist.appendChild(row);
      });
    }
    function loadCredit() {
      var c = sbClient();
      creditConfirmed = {}; creditPending = {};
      if (!c) { loadPosted(); return; }
      c.rpc('get_my_credit_balance').then(function (r) {
        var d = (r && !r.error && r.data) ? r.data : null;
        if (d) {
          tickCredit(d.balance_cents);
          if (creditPendingEl) {
            var pc = Number(d.pending_cents) || 0;
            if (pc > 0) { creditPendingEl.textContent = ' · ' + fmtMoney(pc) + ' pending'; creditPendingEl.hidden = false; }
            else creditPendingEl.hidden = true;
          }
          if (creditWrap) creditWrap.hidden = false;
          var hist = d.history || [];
          renderCreditHist(hist);
          hist.forEach(function (h) {
            if (!h.submission_id) return;
            if (h.status === 'pending') creditPending[h.submission_id] = (creditPending[h.submission_id] || 0) + (h.amount_cents || 0);
            else creditConfirmed[h.submission_id] = (creditConfirmed[h.submission_id] || 0) + (h.amount_cents || 0);
          });
        }
        loadPosted(); /* posts render with the earned-credit maps ready */
      }, function () { loadPosted(); });
    }
    function hideCredit() { creditConfirmed = {}; creditPending = {}; if (creditWrap) creditWrap.hidden = true; if (creditPendingEl) creditPendingEl.hidden = true; }

    function suUrl(path) {
      var c = sbClient(); if (!c || !path) return '';
      try { return c.storage.from('seller-uploads').getPublicUrl(path).data.publicUrl; } catch (e) { return ''; }
    }
    function postCard(row) {
      var card = document.createElement('div'); card.className = 'mcl-post';
      var path = (row.image_paths && row.image_paths[0]) ? row.image_paths[0] : '';
      var url = path ? suUrl(path) : '';
      var img = document.createElement('img'); img.className = 'mcl-post-img'; img.loading = 'lazy'; img.alt = row.title || '';
      if (url) img.src = url;
      var meta = document.createElement('div'); meta.className = 'mcl-post-meta';
      var t = document.createElement('span'); t.className = 'mcl-post-title'; t.textContent = (row.title || 'untitled').toLowerCase();
      var st = row.status || 'pending';
      var chip = document.createElement('span'); chip.className = 'mcl-chip is-' + st; chip.textContent = st;
      meta.appendChild(t); meta.appendChild(chip);
      if (st === 'pending') {
        var s = document.createElement('span'); s.className = 'mcl-post-stat'; s.textContent = 'in review'; meta.appendChild(s);
      } else if (st !== 'rejected') { /* rejected → chip only */
        var n = parseInt(row.click_count, 10) || 0;
        var s2 = document.createElement('span'); s2.className = 'mcl-post-stat'; s2.textContent = n + (n === 1 ? ' click' : ' clicks'); meta.appendChild(s2);
      }
      /* earning badge + credit lines DORMANT (2026-08 save-first rewire): the credit rail is
         hidden platform-wide, so no per-post earn/credit copy renders. code kept for revival.
      var earn = document.createElement('span');
      earn.className = 'mcl-post-earn ' + (row.monetizable ? 'is-earn' : 'is-noearn');
      earn.textContent = row.monetizable ? '✓ earns on sales' : 'no sales credit yet';
      meta.appendChild(earn);
      var conf = creditConfirmed[row.id];
      if (conf) { var ce = document.createElement('span'); ce.className = 'mcl-post-credit'; ce.textContent = 'earned +' + fmtMoney(conf); meta.appendChild(ce); }
      var pend = creditPending[row.id];
      if (pend) { var pe = document.createElement('span'); pe.className = 'mcl-post-pending'; pe.textContent = fmtMoney(pend) + ' pending'; meta.appendChild(pe); }
      */
      card.appendChild(img); card.appendChild(meta);
      return card;
    }
    /* lists tab — the same submissions as a shopping-style image tile. links to the piece's
       product_url when present; the seller-upload image is the plate. status chip for non-live. */
    function listile(row) {
      var path = (row.image_paths && row.image_paths[0]) ? row.image_paths[0] : '';
      var url = path ? suUrl(path) : '';
      var st = row.status || 'pending';
      var dest = row.product_url || '';
      var node = document.createElement(dest ? 'a' : 'div');
      node.className = 'mcl-listile';
      if (dest) { node.href = dest; node.target = '_blank'; node.rel = 'noopener nofollow'; }
      node.setAttribute('aria-label', (row.title || 'posted piece').toLowerCase());
      if (url) { var im = document.createElement('img'); im.loading = 'lazy'; im.alt = row.title || ''; im.src = url; node.appendChild(im); }
      if (st === 'pending' || st === 'rejected') {
        var chip = document.createElement('span'); chip.className = 'mcl-listile-chip is-' + st; chip.textContent = (st === 'pending' ? 'in review' : 'not live'); node.appendChild(chip);
      }
      var tag = document.createElement('span'); tag.className = 'mcl-listile-tag';
      tag.textContent = (row.brand ? row.brand + ' — ' : '') + (row.title || 'untitled');
      node.appendChild(tag);
      return node;
    }
    /* Lists tab (2026-08 save-first rewire): hidden in the markup by default — only members who
       actually have posted tiles (approved buyers) get the tab at all. everyone else's account
       is saves-first with no posting surface. */
    var listsTab = rootEl.querySelector('[data-mcl-tab="tiles"]');
    function showListsTab(on) {
      if (!listsTab) return;
      listsTab.hidden = !on;
      /* if the tab vanishes while it's the active pane (e.g. sign-out), fall back to saves */
      if (!on && listsTab.classList.contains('is-on')) {
        var savesTab = rootEl.querySelector('[data-mcl-tab="saves"]');
        if (savesTab) savesTab.click();
      }
    }
    function loadPosted() {
      var c = sbClient(); if (!c) return;
      c.rpc('get_my_posted_tiles').then(function (r) {
        var rows = (r && !r.error && r.data) ? r.data : [];
        showListsTab(rows.length > 0);
        if (postedGrid) { postedGrid.innerHTML = ''; rows.forEach(function (row) { postedGrid.appendChild(postCard(row)); }); }
        if (postedEmpty) postedEmpty.hidden = rows.length > 0;
        if (listsGrid) { listsGrid.innerHTML = ''; rows.forEach(function (row) { listsGrid.appendChild(listile(row)); }); }
        if (listsEmpty) listsEmpty.hidden = rows.length > 0;
      }, function () {
        showListsTab(false);
        if (postedGrid) postedGrid.innerHTML = ''; if (postedEmpty) postedEmpty.hidden = false;
        if (listsGrid) listsGrid.innerHTML = ''; if (listsEmpty) listsEmpty.hidden = false;
      });
    }
    /* signed out → empty states; the Lists tab stays hidden */
    function hidePosted() {
      showListsTab(false);
      if (postedGrid) postedGrid.innerHTML = ''; if (postedEmpty) postedEmpty.hidden = false;
      if (listsGrid) listsGrid.innerHTML = ''; if (listsEmpty) listsEmpty.hidden = false;
    }

    var itries = 0;
    (function bootId() {
      if (!(window.MIEN && MIEN.ready && MIEN.ready())) { if (++itries < 60) setTimeout(bootId, 150); return; }
      /* ---- first-run onboarding: niches → how-to-list. shown once; skippable everywhere; never gates. ---- */
      var onbEl = rootEl.querySelector('[data-mcl-onb]');
      var onbShown = false;
      function onbInit() {
        if (!onbEl) return function () {};
        var steps = { 1: onbEl.querySelector('[data-mcl-onb-step="1"]'), 2: onbEl.querySelector('[data-mcl-onb-step="2"]') };
        var dots = [].slice.call(onbEl.querySelectorAll('[data-mcl-onb-dots] span'));
        var chips = [].slice.call(onbEl.querySelectorAll('.mcl-onb-chip'));
        var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        var closing = false;
        function setStep(n) {
          if (steps[1]) steps[1].hidden = n !== 1;
          if (steps[2]) steps[2].hidden = n !== 2;
          dots.forEach(function (d, i) { d.classList.toggle('is-on', i === (n - 1)); });
        }
        function picks() {
          return chips.filter(function (c) { return c.classList.contains('is-on'); })
            .map(function (c) { return c.getAttribute('data-niche'); });
        }
        function markDone(extra) { /* stamp onboarded_at (+ optional niches) so it never returns */
          if (!(window.MIEN && MIEN.setProfile)) return;
          var fields = { onboarded_at: new Date().toISOString() };
          if (extra) { for (var k in extra) fields[k] = extra[k]; }
          try { Promise.resolve(MIEN.setProfile(fields)).catch(function () {}); } catch (e) {}
        }
        function close(save, go) {
          if (closing) return; closing = true;
          if (save !== false) markDone(save || null);
          onbEl.classList.remove('is-in'); onbEl.setAttribute('aria-hidden', 'true');
          var fin = function () { onbEl.hidden = true; if (go) window.location.href = go; };
          if (reduce || go) fin(); else setTimeout(fin, 260);
        }
        chips.forEach(function (c) { c.addEventListener('click', function () { c.classList.toggle('is-on'); }); });
        [].slice.call(onbEl.querySelectorAll('[data-mcl-onb-skip]')).forEach(function (b) {
          b.addEventListener('click', function () { close({}); }); /* x / skip / tap-outside → done, no niches */
        });
        var nextBtn = onbEl.querySelector('[data-mcl-onb-next]');
        if (nextBtn) nextBtn.addEventListener('click', function () {
          var n = picks(); markDone(n.length ? { style_niches: n } : null); setStep(2);
        });
        /* save-first rewire (2026-08): the step-2 button now closes into the feed — the old
           '/pages/sell?list=1' listing deep-link is retired for regular members */
        var listBtn = onbEl.querySelector('[data-mcl-onb-list]');
        if (listBtn) listBtn.addEventListener('click', function () { close(false, '/'); });
        return function open() {
          if (onbShown) return; onbShown = true;
          setStep(1); onbEl.hidden = false; onbEl.setAttribute('aria-hidden', 'false');
          if (reduce) { onbEl.classList.add('is-in'); return; }
          requestAnimationFrame(function () { requestAnimationFrame(function () { onbEl.classList.add('is-in'); }); });
        };
      }
      var openOnboard = onbInit();
      /* the global first-run flow (mien-onboarding snippet, window.MienOnboarding) is now the
         canonical intro — it captures style via this-or-that and owns the once-only trigger. when
         it's present, stand this legacy niche popup down so the two never stack on the Account page.
         (kept intact / reversible: it fires again if the global flow is ever removed.) */
      function maybeOnboard(pr) { return; /* ALL onboarding retired (2026-08 strip-down) — the global
        flow is un-rendered and this legacy niche popup must not resurface in its place */ }

      var authPrompted = false;
      function paint(sess) {
        if (!sess || !sess.user) { hideIdentity(); hidePosted(); hideCredit(); promptSignedOut(); return; }
        signedInNow = true;
        if (profEl) profEl.hidden = false;
        if (nameEdit) nameEdit.hidden = false;
        if (quietEl) quietEl.hidden = false;
        if (avBtn) avBtn.classList.add('can-edit');
        paintProfile(null, sess.user.email);
        MIEN.profile().then(function (pr) { if (pr) { paintProfile(pr, sess.user.email); maybeOnboard(pr); } }).catch(function () {});
        loadCredit(); /* history + per-post maps (credit UI itself is hidden); then loadPosted() */
      }
      /* signed-out visitors hitting /account → the reusable signup modal (once, dismissible so
         they can still browse their local saves). never a blank/broken page. */
      function promptSignedOut() {
        if (authPrompted) return; authPrompted = true;
        if (window.MienAuthModal && MienAuthModal.open && !(MienAuthModal.isOpen && MienAuthModal.isOpen())) {
          MienAuthModal.open({ mode: 'create', source: 'account' });
        }
      }
      MIEN.session().then(paint).catch(function () {});
      MIEN.onChange(paint);
      var out = rootEl.querySelector('[data-mcl-signout]');
      if (out) out.addEventListener('click', function () { MIEN.signOut(); });

      /* avatar upload → avatars bucket → profiles.avatar_url */
      if (avBtn && avInput) {
        avBtn.addEventListener('click', function () { if (signedInNow) avInput.click(); });
        avInput.addEventListener('change', function () {
          var f = avInput.files && avInput.files[0];
          avInput.value = '';
          if (!f || !(window.MIEN && MIEN.uploadAvatar)) return;
          if (f.type.indexOf('image/') !== 0) return;
          if (avBtn) avBtn.classList.add('is-busy');
          MIEN.uploadAvatar(f).then(function (r) {
            if (avBtn) avBtn.classList.remove('is-busy');
            if (r && r.url) { paintAvatar(r.url, nameEl ? nameEl.textContent : 'm'); if (MIEN.setProfile) MIEN.setProfile({ avatar_url: r.url }); }
          }).catch(function () { if (avBtn) avBtn.classList.remove('is-busy'); });
        });
      }

      /* inline name edit → profiles.display_name. the pencil goes STRAIGHT into editing (no
         popover menu) — the input takes the headline's place; a tiny hint says how to save. */
      if (nameInput && nameEl) {
        var editing = false;
        function openEdit() {
          if (!signedInNow) return;
          editing = true;
          nameInput.value = (nameEl.textContent || '').trim();
          nameEl.hidden = true; nameInput.hidden = false;
          if (nameEdit) nameEdit.hidden = true;
          if (nameHint) nameHint.hidden = false;
          nameInput.focus(); nameInput.select();
        }
        function closeEdit(save) {
          if (!editing) return; editing = false;
          var v = (nameInput.value || '').trim().slice(0, 40);
          nameInput.hidden = true; nameEl.hidden = false;
          if (nameEdit) nameEdit.hidden = !signedInNow;
          if (nameHint) nameHint.hidden = true;
          if (save && v) { nameEl.textContent = v; if (window.MIEN && MIEN.setProfile) MIEN.setProfile({ display_name: v }); }
        }
        if (nameEdit) nameEdit.addEventListener('click', openEdit);
        nameInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); closeEdit(true); }
          else if (e.key === 'Escape') { e.preventDefault(); closeEdit(false); }
        });
        nameInput.addEventListener('blur', function () { closeEdit(true); });
      }
    })();
  }

  /* ============================================================
     comments — tile pages. supabase-backed, same community auth.
     public read; posting needs login (else a quiet line → /pages/community).
     newest-first, cap 10 + show-more, optimistic insert w/ rollback,
     author soft-delete. client guards: trim + non-empty + 500 cap.
     ============================================================ */
  function commentsInit() {
    var root = document.querySelector('[data-mtc]');
    if (!root) return;
    var handle = root.getAttribute('data-handle');
    var listEl = root.querySelector('[data-mtc-list]');
    var emptyEl = root.querySelector('[data-mtc-empty]');
    var formEl = root.querySelector('[data-mtc-form]');
    var inputEl = root.querySelector('[data-mtc-input]');
    var authEl = root.querySelector('[data-mtc-auth]');
    var postBtn = root.querySelector('[data-mtc-post]');
    if (!handle || !listEl) return;

    var all = [], uid = null, names = {}, posting = false; /* names: user_id -> profiles.handle */
    var isAdmin = false;          /* is_mien_admin() — admin can delete ANY comment */
    var expanded = false;         /* collapse past 3 behind "show more" */
    var COLLAPSE = 3;
    var DRAFT_KEY = 'mien_comment_draft:' + handle; /* survives the signup round-trip */

    function sb() { return (window.MIEN && MIEN.sb) ? MIEN.sb : null; }
    function esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
    function rel(ts) {
      var t = new Date(ts).getTime();
      if (isNaN(t)) return '';
      var s = Math.max(0, (Date.now() - t) / 1000);
      if (s < 60) return 'just now';
      var m = s / 60; if (m < 60) return Math.floor(m) + 'm';
      var h = m / 60; if (h < 24) return Math.floor(h) + 'h';
      var d = h / 24; if (d < 7) return Math.floor(d) + 'd';
      var w = d / 7; if (w < 5) return Math.floor(w) + 'w';
      var mo = d / 30; if (mo < 12) return Math.floor(mo) + 'mo';
      return Math.floor(d / 365) + 'y';
    }
    function nameOf(c) { return names[c.user_id] || 'mien member'; }
    function rowEl(c) {
      var row = document.createElement('div');
      row.className = 'mtc-row' + (c._pending ? ' is-pending' : '');
      var canDel = !c._pending && uid && (c.user_id === uid || isAdmin);
      row.innerHTML =
        '<div class="mtc-meta">' +
          '<span class="mtc-who">' + esc(nameOf(c)) + '</span>' +
          '<span class="mtc-time">' + esc(rel(c.created_at)) + '</span>' +
          (canDel ? '<button class="mtc-del" type="button" data-mtc-del="' + esc(c.id) + '" aria-label="delete comment">&times;</button>' : '') +
        '</div>' +
        '<p class="mtc-body">' + esc(c.body) + '</p>';
      return row;
    }
    function paint() {
      listEl.innerHTML = '';
      var show = expanded ? all : all.slice(0, COLLAPSE);
      show.forEach(function (c) { listEl.appendChild(rowEl(c)); });
      if (!expanded && all.length > COLLAPSE) {
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'mtc-more';
        more.setAttribute('data-mtc-more', '');
        more.textContent = 'show ' + (all.length - COLLAPSE) + ' more';
        listEl.appendChild(more);
      }
      if (emptyEl) emptyEl.hidden = all.length > 0;
    }
    function canPost() { return !!uid && !!inputEl && inputEl.value.trim().length > 0; }
    function syncBtn() { if (postBtn) postBtn.disabled = posting || !canPost(); }

    /* batch the profile-name lookup for any user_ids we don't have yet */
    function loadNames(rows, done) {
      var need = {};
      rows.forEach(function (c) { if (c.user_id && !(c.user_id in names)) need[c.user_id] = 1; });
      var ids = Object.keys(need);
      if (!ids.length) { if (done) done(); return; }
      var client = sb();
      if (!client) { ids.forEach(function (id) { names[id] = 'mien member'; }); if (done) done(); return; }
      client.from('profiles').select('id, handle').in('id', ids).then(function (r) {
        (r && r.data ? r.data : []).forEach(function (p) { names[p.id] = p.handle || 'mien member'; });
        ids.forEach(function (id) { if (!(id in names)) names[id] = 'mien member'; });
        if (done) done();
      }, function () { ids.forEach(function (id) { names[id] = 'mien member'; }); if (done) done(); });
    }

    function fetchComments() {
      var client = sb(); if (!client) return;
      client.from('product_comments')
        .select('id, user_id, body, created_at')
        .eq('product_handle', handle)
        .eq('hidden', false)
        .order('created_at', { ascending: false })
        .then(function (r) { all = (r && r.data) ? r.data : []; loadNames(all, paint); }, function () {});
    }

    function submit() {
      if (posting || !canPost()) return;
      var body = inputEl.value.trim();
      if (body.length > 500) body = body.slice(0, 500);
      var client = sb(); if (!client) return;
      var prevText = inputEl.value;
      inputEl.value = ''; inputEl.style.height = '';
      posting = true; syncBtn();
      var temp = { id: 'tmp-' + Date.now(), user_id: uid, body: body, created_at: new Date().toISOString(), _pending: true };
      all.unshift(temp); paint();
      function done() { posting = false; syncBtn(); }
      function rollback() { var i = all.indexOf(temp); if (i > -1) all.splice(i, 1); inputEl.value = prevText; paint(); done(); }
      client.from('product_comments')
        .insert({ user_id: uid, product_handle: handle, body: body })
        .select('id, user_id, body, created_at').single()
        .then(function (r) {
          if (r && r.error) { rollback(); return; }
          var i = all.indexOf(temp);
          if (r && r.data && i > -1) all[i] = r.data;
          else if (i > -1) temp._pending = false;
          paint(); done();
        }, rollback);
    }

    if (formEl) formEl.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    if (inputEl) inputEl.addEventListener('input', function () {
      if (inputEl.value.length > 500) inputEl.value = inputEl.value.slice(0, 500);
      inputEl.style.height = 'auto'; inputEl.style.height = Math.min(inputEl.scrollHeight, 168) + 'px';
      syncBtn();
      /* keep the draft warm so it survives a signup round-trip mid-thought */
      try { sessionStorage.setItem(DRAFT_KEY, inputEl.value); } catch (e2) {}
    });
    /* signed-out tap on the composer → stash the draft + signup modal; the draft is
       restored (and focus returned) the moment the session lands in apply() below */
    if (inputEl) inputEl.addEventListener('focus', function () {
      if (uid) return;
      try { sessionStorage.setItem(DRAFT_KEY, inputEl.value); } catch (e2) {}
      inputEl.blur();
      if (window.MienAuthModal && MienAuthModal.open) MienAuthModal.open({ mode: 'create', source: 'comment' });
    });
    /* show more + delete (own, or any for admin) — delegated */
    listEl.addEventListener('click', function (e) {
      var more = e.target.closest('[data-mtc-more]');
      if (more) { expanded = true; paint(); return; }
      var del = e.target.closest('[data-mtc-del]');
      if (!del) return;
      var id = del.getAttribute('data-mtc-del');
      var client = sb(); if (!client || !id) return;
      var i = -1;
      all.some(function (c, idx) { if (String(c.id) === id) { i = idx; return true; } return false; });
      if (i === -1) return;
      var removed = all.splice(i, 1)[0];
      paint(); /* optimistic — restored on failure */
      client.from('product_comments').delete().eq('id', id).then(function (r) {
        if (r && r.error) { all.splice(i, 0, removed); paint(); }
      }, function () { all.splice(i, 0, removed); paint(); });
    });

    var tries = 0;
    (function boot() {
      if (!(window.MIEN && MIEN.ready && MIEN.ready())) { if (++tries < 60) setTimeout(boot, 150); return; }
      function apply(s) {
        var hadUid = !!uid;
        uid = (s && s.user) ? s.user.id : null;
        /* the composer is ALWAYS visible — signed-out taps route through the signup modal
           (focus handler above) instead of hiding the input behind a link */
        if (formEl) formEl.hidden = false;
        if (authEl) authEl.hidden = true;
        if (uid && !(uid in names)) {
          MIEN.sb.from('profiles').select('id, handle').eq('id', uid).maybeSingle().then(function (r) {
            names[uid] = (r && r.data && r.data.handle) ? r.data.handle : 'mien member'; paint();
          }, function () {});
        }
        if (uid && !hadUid) {
          /* fresh sign-in: return them to the comment they were writing */
          var draft = '';
          try { draft = sessionStorage.getItem(DRAFT_KEY) || ''; } catch (e2) {}
          if (draft && inputEl && !inputEl.value) {
            inputEl.value = draft;
            inputEl.style.height = 'auto'; inputEl.style.height = Math.min(inputEl.scrollHeight, 168) + 'px';
            try { inputEl.focus(); } catch (e3) {}
          }
          /* admin check — reveals delete on every comment for Reid */
          try {
            MIEN.sb.rpc('is_mien_admin').then(function (r) {
              isAdmin = !!(r && !r.error && r.data === true);
              if (isAdmin) paint();
            }, function () {});
          } catch (e4) {}
        }
        if (!uid) isAdmin = false;
        syncBtn(); paint();
      }
      /* "sign in to comment" opens the shared auth modal (the old /pages/community is retired);
         onChange(apply) below reveals the form the moment the session lands. */
      var signinBtn = root.querySelector('[data-mtc-signin]');
      if (signinBtn) signinBtn.addEventListener('click', function () {
        if (window.MienAuthModal && MienAuthModal.open) MienAuthModal.open({ mode: 'signin', source: 'comment' });
      });
      MIEN.session().then(apply).catch(function () { if (authEl) authEl.hidden = false; });
      MIEN.onChange(apply);
      fetchComments();
    })();
  }

  /* ============================================================
     product-page gallery — image-count driven. 1 image = static hero (no chrome);
     2+ = scroll-snap swipe; dots (≤6) or "1/8" counter (>6); desktop prev/next.
     ============================================================ */
  /* product-page gallery — Kith-style thumbnail rail: click a thumb → swap the dominant
     main image. single-image pages have no thumbs, so this no-ops.
     2026-08-27: index-based show() + touch swipe on the main image (the rail alone
     read as broken on phones — thumbs lazy-load in blank and nobody taps them), a
     quiet "1/8" counter chip, neighbour preloading so swipes land instantly, and the
     rail scrolls its active thumb into view. swipe detection is touchend-delta only
     (passive listeners, no preventDefault) so vertical page scrolling stays native. */
  function tileGalleryInit() {
    var gallery = document.querySelector('[data-mtp-gallery]');
    if (!gallery) return;
    var mainImg = gallery.querySelector('[data-mtp-main-img]');
    var thumbs = [].slice.call(gallery.querySelectorAll('[data-mtp-thumb]'));
    if (!mainImg || thumbs.length < 2) return;
    var counter = gallery.querySelector('[data-mtp-count]');
    var cur = 0;
    function preload(i) {
      var full = thumbs[(i + thumbs.length) % thumbs.length].getAttribute('data-full');
      if (full) (new Image()).src = full;
    }
    function show(i) {
      i = (i + thumbs.length) % thumbs.length;
      var t = thumbs[i];
      cur = i;
      var full = t.getAttribute('data-full');
      if (full) { mainImg.removeAttribute('srcset'); mainImg.src = full; }
      var w = t.getAttribute('data-w'), h = t.getAttribute('data-h');
      if (w) mainImg.setAttribute('width', w);
      if (h) mainImg.setAttribute('height', h);
      thumbs.forEach(function (x) { x.classList.toggle('is-on', x === t); });
      if (t.scrollIntoView) { try { t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } catch (_) {} }
      if (counter) counter.textContent = (i + 1) + '/' + thumbs.length;
      preload(i + 1); preload(i - 1);
    }
    gallery.addEventListener('click', function (e) {
      var t = e.target.closest('[data-mtp-thumb]');
      if (!t) return;
      show(thumbs.indexOf(t));
    });
    var main = gallery.querySelector('.mtp-main') || mainImg;
    var sx = 0, sy = 0, armed = false;
    main.addEventListener('touchstart', function (e) {
      armed = e.touches.length === 1;
      if (armed) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
    }, { passive: true });
    main.addEventListener('touchend', function (e) {
      if (!armed) return;
      armed = false;
      var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      show(cur + (dx < 0 ? 1 : -1));
    }, { passive: true });
    preload(1);
  }

  /* ============================================================
     feed category filter — client-side visibility over data-tags (category:<slug>).
     button + drawer live in the header (homepage). single-select. hidden tiles drop
     out of the grid so the masonry reflows; re-applied to every appended batch.
     tier/lane/source/featured/affiliate behavior is untouched.
     ============================================================ */
  /* finer "item" filters (2026-08-10) — beyond the coarse category: tags, these match
     the tile's TITLE (the plate link's aria-label carries "title — brand") so items
     like tees / hoodies / denim are findable without retagging the catalog. a slug
     matches when the tile carries category:<slug> OR its title hits the rule. */
  var ITEM_RULES = {
    tees:        { re: /\bt-?shirts?\b|\btees?\b|tank top|\bfitted t\b/ },
    hoodies:     { re: /hoodie|hooded sweat/ },
    sweatshirts: { re: /sweatshirt|crew ?neck|\bcrew\b/, not: /hood/ },
    jerseys:     { re: /jersey/ },
    jackets:     { re: /jacket|blazer|bomber|windbreaker|anorak|parka|puffer|smock/ },
    denim:       { re: /denim|\bjeans?\b/, not: /\bhats?\b|beanie|\bcaps?\b/ }
  };
  function tileTitle(tile) {
    var a = tile.querySelector('.mfeed-plate');
    return ((a && a.getAttribute('aria-label')) || '').toLowerCase();
  }
  function tileHasItem(tile, slug, tags) {
    if (tags.indexOf('category:' + slug) !== -1) return true;
    var r = ITEM_RULES[slug];
    if (!r) return false;
    var t = tileTitle(tile);
    if (r.not && r.not.test(t)) return false;
    return r.re.test(t);
  }

  /* ============================================================
     MienTaste (2026-08-10) — a per-member style profile that biases the feed
     arrangement. features come from what every tile already carries (color:/
     category: tags, brand, title item-rules); weights come from the 5-slide
     this-or-that onboarding plus saved hearts. lives in localStorage
     (mien_taste_v1) and mirrors to profiles.style_picks (jsonb) when signed
     in, so taste follows the account across devices. scoring only ever
     REORDERS the feed — nothing is hidden, discovery stays.
     ============================================================ */
  var MienTaste = (function () {
    var KEY = 'mien_taste_v1';
    var state = null;
    function load() {
      if (state) return state;
      try { state = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { state = null; }
      if (!state || typeof state !== 'object' || typeof state.w !== 'object' || !state.w) state = { w: {}, done: false, at: 0 };
      return state;
    }
    function persist() {
      try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
      /* mirror to the account, fire-and-forget */
      try {
        if (window.MIEN && MIEN.ready && MIEN.ready() && MIEN.setProfile) {
          MIEN.setProfile({ style_picks: { v: 1, w: state.w, done: state.done, at: new Date().toISOString() } })
            .then(function () {}, function () {});
        }
      } catch (e) {}
    }
    /* signed-in on a fresh browser: adopt the account's stored vector once */
    function adoptProfile() {
      try {
        if (!(window.MIEN && MIEN.ready && MIEN.ready() && MIEN.profile)) return;
        var s = load();
        if (s.done || Object.keys(s.w).length) return;
        MIEN.profile().then(function (p) {
          var sp = p && p.style_picks;
          if (sp && sp.w) {
            state = { w: sp.w, done: !!sp.done, at: Date.now() };
            try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
          }
        }, function () {});
      } catch (e) {}
    }
    function featuresOf(tile) {
      var f = [];
      (tile.getAttribute('data-tags') || '').split(',').forEach(function (tag) {
        if (tag.indexOf('color:') === 0) f.push('col:' + tag.slice(6));
        else if (tag.indexOf('category:') === 0) f.push('cat:' + tag.slice(9));
      });
      var b = tile.querySelector('.mfeed-brand');
      if (b) f.push('brand:' + b.textContent.trim().toLowerCase());
      var t = tileTitle(tile);
      Object.keys(ITEM_RULES).forEach(function (slug) {
        var r = ITEM_RULES[slug];
        if (r.not && r.not.test(t)) return;
        if (r.re.test(t)) f.push('item:' + slug);
      });
      return f;
    }
    /* this-or-that: reward what they tapped, gently dock what they passed on —
       but only where the two actually differ (shared features teach nothing) */
    function learn(winnerTile, loserTile) {
      var s = load();
      var wf = featuresOf(winnerTile);
      var lf = loserTile ? featuresOf(loserTile) : [];
      var shared = {};
      wf.forEach(function (f) { if (lf.indexOf(f) !== -1) shared[f] = true; });
      wf.forEach(function (f) { if (!shared[f]) s.w[f] = (s.w[f] || 0) + 1; });
      lf.forEach(function (f) { if (!shared[f]) s.w[f] = (s.w[f] || 0) - 0.4; });
      persist();
    }
    function finish() { var s = load(); s.done = true; s.at = Date.now(); persist(); }
    function score(tile, extraW) {
      var s = load(), total = 0;
      featuresOf(tile).forEach(function (f) {
        total += (s.w[f] || 0) + (extraW && extraW[f] ? extraW[f] : 0);
      });
      return total;
    }
    function hasTaste() { return Object.keys(load().w).length > 0; }
    /* taste-biased stable reorder — keeps the seeded arrangement's local texture,
       pulls leaned-toward tiles up to ~a third of the list earlier. rng is the
       session-seeded prng, so the result stays stable within a session. */
    function rank(list, rng) {
      var extraW = {};
      /* saved hearts are a live signal even before onboarding runs */
      try {
        if (typeof MienCloset !== 'undefined' && MienCloset.has) {
          list.forEach(function (t) {
            var h = t.getAttribute('data-handle');
            if (h && MienCloset.has(h)) featuresOf(t).forEach(function (f) { extraW[f] = (extraW[f] || 0) + 0.35; });
          });
        }
      } catch (e) {}
      if (!hasTaste() && !Object.keys(extraW).length) return list;
      var scores = list.map(function (t) { return score(t, extraW); });
      var min = Math.min.apply(null, scores), max = Math.max.apply(null, scores);
      if (!(max > min)) return list;
      var n = list.length || 1;
      var keyed = list.map(function (t, i) {
        var norm = (scores[i] - min) / (max - min);
        return { t: t, k: i / n - 0.35 * norm + (rng ? rng() : Math.random()) * 0.06 };
      });
      keyed.sort(function (a, b) { return a.k - b.k; });
      return keyed.map(function (x) { return x.t; });
    }
    return { featuresOf: featuresOf, learn: learn, finish: finish, rank: rank,
      done: function () { return !!load().done; }, hasTaste: hasTaste, adoptProfile: adoptProfile };
  })();
  window.MienTaste = MienTaste;

  var FeedFilter = (function () {
    var current = '';           /* legacy single-category slug (retired drawer api) */
    var fCat = '', fCol = '';   /* combined filter-bar filters (2026-08-10): item AND color */
    var fNat = false;           /* "shop on mien" — native/collective tiles only (2026-08-24) */
    function tileMatches(tile) {
      var tags = (tile.getAttribute('data-tags') || '').split(',');
      /* echo tiles are the position-floor's repeat appearances (data-mfeed-echo, see
         feedInit) — real in the default scroll, duplicates the moment ANY filter
         collapses the feed, so a filtered view hides them and keeps one of each. */
      if ((current || fCat || fCol || fNat) && tile.hasAttribute('data-mfeed-echo')) return false;
      if (fNat && !isNativeTile(tile)) return false;
      if (current && !tileHasItem(tile, current, tags)) return false;
      if (fCat && !tileHasItem(tile, fCat, tags)) return false;
      if (fCol && tags.indexOf('color:' + fCol) === -1) return false;
      return true;
    }
    /* toggle a class (not [hidden]) — `.mfeed-grid .mfeed-tile { display:block }` outranks
       the [hidden] attribute, so hidden never took effect. this rule uses !important. */
    function applyTile(tile) { tile.classList.toggle('mff-hide', !tileMatches(tile)); }
    /* empty state — while a category is active but every loaded tile is hidden, show
       "nothing here yet". it self-hides as matching tiles arrive from later batches. */
    function refreshEmpty() {
      var empty = document.querySelector('[data-mfeed-empty]');
      if (!empty) return;
      var visible = document.querySelector('.mfeed-grid > .mfeed-tile:not(.mff-hide)');
      var active = current || fCat || fCol || fNat;
      var show = !!(active && !visible);
      empty.hidden = !show;
      /* don't stack "you've seen everything" under the empty note */
      if (show) { var end = document.querySelector('[data-mfeed-end]'); if (end) end.hidden = true; }
    }
    function applyAll() {
      document.querySelectorAll('.mfeed-grid > .mfeed-tile').forEach(applyTile);
      document.querySelectorAll('.mfeed-grid.is-masonry').forEach(layoutGrid);
      refreshEmpty();
    }
    return { current: function () { return current; }, set: function (s) { current = s || ''; applyAll(); },
      setFilters: function (cat, col) { fCat = cat || ''; fCol = col || ''; applyAll(); },
      /* the native toggle is a third AND-axis: it stacks with category + colour, never replaces them */
      setNative: function (on) { fNat = !!on; applyAll(); },
      filters: function () { return { category: fCat, color: fCol, native: fNat }; },
      applyTile: applyTile, refreshEmpty: refreshEmpty };
  })();
  window.FeedFilter = FeedFilter;

  /* ============================================================
     shop-by filters (2026-08-10) — color dots + item pills, now living in the
     hamburger sidebar (mien-header.liquid renders the full sets statically).
     on feed/search pages this init owns them: values without products behind
     them are hidden (data-tags census), a tap applies the filter client-side,
     closes the menu so the result is visible, and paints an active chip on the
     feed with a clear ×. /?fcol=…&fcat=… deep-links (set by the header's
     gridless fallback) are read back on load. combined AND via
     FeedFilter.setFilters; clearing unhides everything — the session-seeded
     arrangement, scroll position and pagination are untouched.
     ============================================================ */
  function filterBarInit() {
    var bar = document.querySelector('[data-mflt]');
    if (!bar) return;
    /* only feed/search render [data-mfeed]; elsewhere (incl. the closet, whose grid
       reuses .mfeed-grid) the header's fallback routes taps to the feed instead */
    if (!document.querySelector('[data-mfeed]')) return;
    /* 2026-10-23: the sidebar pills are the eight fly categories now (clothing ... other), which
       are not product category: tags, so filtering a product grid by them would hide everything.
       stand down and let the header fallback route the tap to the fly feed (/?fcat=). */
    if (bar.querySelector('[data-cat="clothing"]')) return;
    var catBtns = bar.querySelectorAll('[data-cat]');
    var natBtn = bar.querySelector('[data-mflt-nat]'); /* the "shop on mien" chip */
    var clearBtn = bar.querySelector('[data-mflt-clear]');
    var colBtns = bar.querySelectorAll('[data-col]'); /* the swatch scroller */
    var pickedWrap = bar.querySelector('[data-mflt-picked]');
    var pickedDot = bar.querySelector('[data-mflt-picked-dot]');
    var pickedName = bar.querySelector('[data-mflt-picked-name]');
    var SW = { black: '#161616', white: '#f6f4ef', grey: '#9a9a9a', brown: '#8a6a44', red: '#c03434',
      pink: '#e793b4', orange: '#e08334', yellow: '#e3c94a', green: '#4a7d4a', blue: '#4a63b0',
      purple: '#8257a8', multi: 'conic-gradient(#c03434, #e3c94a, #4a7d4a, #4a63b0, #c03434)' };
    /* WHAT THE SHOP ACTUALLY HAS, from the server, once (see mien-header's data-mflt-avail).
       Reading availability off the loaded tiles alone was the bug Reid hit: the feed renders
       24 tiles at a time, so the first paint only ever saw a handful of categories and chips
       kept appearing and disappearing as more pages streamed in. the collection's own tag list
       is complete from the first byte. */
    var catsWrap = bar.querySelector('[data-mflt-cats]');
    var serverTags = ((catsWrap && catsWrap.getAttribute('data-mflt-avail')) || '')
      .split(',').map(function (t) { return t.trim(); });
    /* and once a chip has been shown it STAYS shown — a control that blinks out from under a
       thumb is worse than one that occasionally leads to an empty grid (which the feed already
       explains with "nothing here for that filter"). */
    var everSeen = {};
    function have() {
      var tiles = Array.prototype.slice.call(document.querySelectorAll('.mfeed-grid > .mfeed-tile'));
      /* item availability runs the same matcher the filter uses (tag OR title rule),
         so finer items like tees/denim count even without a category: tag */
      var cats = {};
      catBtns.forEach(function (b) {
        var slug = b.getAttribute('data-cat');
        var on = serverTags.indexOf('category:' + slug) >= 0 || everSeen[slug] === true
          || tiles.some(function (t) {
            return tileHasItem(t, slug, (t.getAttribute('data-tags') || '').split(','));
          });
        if (on) everSeen[slug] = true;
        cats[slug] = on;
      });
      return { cats: cats };
    }
    function closeMenu() {
      document.body.classList.remove('mhd-open');
      var ov = document.getElementById('mhd-ov');
      if (ov) ov.setAttribute('aria-hidden', 'true');
      var mb = document.querySelector('.mhd-menu');
      if (mb) { mb.setAttribute('aria-expanded', 'false'); mb.setAttribute('aria-label', 'open menu'); }
    }
    function paintActive(f) {
      /* the little chip at the top of the feed — shows what's on, tap = clear all */
      var wrap = document.querySelector('[data-mff-active]');
      var label = document.querySelector('[data-mff-active-label]');
      if (!wrap) return;
      var parts = [];
      if (f.native) parts.push('shop on mien');
      if (f.color) parts.push(f.color);
      if (f.category) parts.push(f.category);
      if (parts.length) { if (label) label.textContent = parts.join(' · '); wrap.hidden = false; }
      else wrap.hidden = true;
    }
    function paint() {
      var c = have();
      var f = FeedFilter.filters();
      catBtns.forEach(function (b) {
        var cat = b.getAttribute('data-cat');
        b.hidden = !c.cats[cat]; /* never an empty filter value */
        b.classList.toggle('is-on', f.category === cat);
        b.setAttribute('aria-pressed', f.category === cat ? 'true' : 'false');
      });
      /* the chosen swatch reads as chosen — the row IS the state, so there's no
         separate "you picked grey" line under it any more */
      colBtns.forEach(function (b) {
        var on = f.color === b.getAttribute('data-col');
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      /* legacy readout, kept guarded in case the picker markup ever comes back */
      if (pickedWrap) {
        if (f.color) {
          if (pickedName) pickedName.textContent = f.color;
          if (pickedDot) pickedDot.style.background = SW[f.color] || '#ccc';
          pickedWrap.hidden = false;
        } else {
          pickedWrap.hidden = true;
        }
      }
      if (natBtn) {
        natBtn.classList.toggle('is-on', !!f.native);
        natBtn.setAttribute('aria-pressed', f.native ? 'true' : 'false');
      }
      if (clearBtn) clearBtn.hidden = !(f.color || f.category || f.native);
      paintActive(f);
    }
    bar.addEventListener('click', function (e) {
      var cb = e.target.closest('[data-cat]');
      var nb = e.target.closest('[data-mflt-nat]');
      var xb = e.target.closest('[data-mflt-clear]');
      if (!cb && !nb && !xb) return;
      var f = FeedFilter.filters();
      if (xb) {
        FeedFilter.setFilters('', '');
        FeedFilter.setNative(false);
      } else if (nb) {
        FeedFilter.setNative(!f.native); /* tap again = clear; stacks with category/colour */
      } else {
        var cat = cb.getAttribute('data-cat');
        FeedFilter.setFilters(f.category === cat ? '' : cat, f.color); /* tap again = clear */
      }
      paint();
      if (!xb) closeMenu(); /* show the result; reopen to stack the other axis */
    });
    /* a tap on the header's colour swatch row lands here */
    document.addEventListener('mien:pickcolor', function (e) {
      var bucket = e.detail && e.detail.bucket;
      if (!bucket) return;
      var f = FeedFilter.filters();
      FeedFilter.setFilters(f.category, f.color === bucket ? '' : bucket); /* tap again = clear */
      paint();
      closeMenu(); /* show the result; reopen to refine the pick */
    });
    /* active-chip × on the feed clears everything */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mff-clear]')) return;
      FeedFilter.setFilters('', '');
      FeedFilter.setNative(false);
      paint();
    });
    /* deep link in from a gridless page: /?fcol=black&fcat=hats&fnat=1 */
    try {
      var sp = new URLSearchParams(window.location.search);
      var qCat = sp.get('fcat') || '', qCol = sp.get('fcol') || '', qNat = sp.get('fnat') || '';
      if (qCat || qCol) FeedFilter.setFilters(qCat, qCol);
      if (qNat) FeedFilter.setNative(true);
    } catch (err) { /* old browsers: no deep link, filters still work */ }
    paint();
    /* re-count as tiles stream in (feed pool rebuild / later search pages) */
    var grid = document.querySelector('[data-mfeed-grid]');
    if (grid && 'MutationObserver' in window) {
      var t = null;
      new MutationObserver(function () {
        clearTimeout(t); t = setTimeout(paint, 250);
      }).observe(grid, { childList: true });
    }
  }

  function feedFilterInit() {
    var openBtn = document.querySelector('[data-mff-open]');
    var drawer = document.getElementById('mff-drawer');
    if (!openBtn || !drawer) return;
    var activeWrap = document.querySelector('[data-mff-active]');
    var activeLabel = document.querySelector('[data-mff-active-label]');

    function setOpen(on) {
      drawer.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('mff-open', on);
      openBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
    }
    function paint() {
      var slug = FeedFilter.current();
      drawer.querySelectorAll('[data-mff-cat]').forEach(function (b) {
        var on = (b.getAttribute('data-mff-cat') || '') === slug;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (activeWrap) {
        if (slug) { if (activeLabel) activeLabel.textContent = slug; activeWrap.hidden = false; }
        else activeWrap.hidden = true;
      }
    }
    function choose(slug) { FeedFilter.set(slug); paint(); setOpen(false); }

    openBtn.addEventListener('click', function () { setOpen(drawer.getAttribute('aria-hidden') === 'true'); });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-mff-close]')) { setOpen(false); return; }
      var cat = e.target.closest('[data-mff-cat]');
      if (cat) choose(cat.getAttribute('data-mff-cat') || '');
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setOpen(false); });
    if (activeWrap) activeWrap.addEventListener('click', function (e) { if (e.target.closest('[data-mff-clear]')) choose(''); });
    paint();
  }

  /* ============================================================
     "chasing" (unknowns) — a member's private list of pieces they can't identify.
     private `unknowns` bucket + submissions(kind='unknown'). signed-out → sign-in prompt
     (like comments). never surfaces status; only shows the answer when resolved + link set.
     ============================================================ */
  function unknownsInit() {
    var root = document.querySelector('[data-mun]');
    if (!root) return;
    var q = function (s) { return root.querySelector(s); };
    var form = q('[data-mun-form]'), fileInput = q('[data-mun-file]'), dropInner = q('[data-mun-drop-inner]'),
        preview = q('[data-mun-preview]'), note = q('[data-mun-note]'), post = q('[data-mun-post]'),
        errEl = q('[data-mun-err]'), authEl = q('[data-mun-auth]'), signinBtn = q('[data-mun-signin]'),
        listEl = q('[data-mun-list]'), emptyEl = q('[data-mun-empty]');
    if (!form || !listEl) return;

    var ALLOWED = { 'image/jpeg': 1, 'image/png': 1, 'image/webp': 1, 'image/heic': 1 };
    var MAX = 10 * 1024 * 1024;
    var picked = null, byPath = {};

    function setErr(m) { if (errEl) { errEl.textContent = (m || '').toLowerCase(); errEl.hidden = !m; } }
    function esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
    function resetPreview() { if (preview) { preview.hidden = true; preview.removeAttribute('src'); } if (dropInner) dropInner.style.display = ''; }
    function validFile(f) {
      if (!f) return 'pick a photo.';
      var okType = ALLOWED[f.type] || /\.(jpe?g|png|webp|heic)$/i.test(f.name || '');
      if (!okType) return 'jpg, png, webp or heic only.';
      if (f.size > MAX) return "that image is over 10mb.";
      return null;
    }
    function refreshEmpty() { if (emptyEl) emptyEl.hidden = listEl.children.length > 0; }

    function rowEl(r) {
      var row = document.createElement('div'); row.className = 'mun-row';
      row.setAttribute('data-id', r.id); row.setAttribute('data-path', r.image_url || '');
      byPath[r.id] = r.image_url || '';
      var thumb = document.createElement('span'); thumb.className = 'mun-thumb';
      var img = document.createElement('img'); img.loading = 'lazy'; img.alt = r.note || 'chasing'; thumb.appendChild(img);
      if (r.image_url && window.MIEN && MIEN.unknownSignedUrl) {
        MIEN.unknownSignedUrl(r.image_url).then(function (u) { if (u) img.src = u; });
      }
      var text = document.createElement('div'); text.className = 'mun-row-text';
      var nt = document.createElement('span'); nt.className = 'mun-row-note';
      nt.textContent = (r.note && r.note.trim()) ? r.note : "don't know what it is";
      text.appendChild(nt);
      /* only surface an answer when resolved + link set — nothing about status otherwise */
      if (r.status === 'resolved' && r.link) {
        var a = document.createElement('a'); a.className = 'mun-answer';
        a.href = r.link; a.target = '_blank'; a.rel = 'noopener nofollow';
        a.textContent = (r.admin_note && r.admin_note.trim()) ? r.admin_note.toLowerCase() : 'found it →';
        text.appendChild(a);
      }
      var del = document.createElement('button'); del.className = 'mun-del'; del.type = 'button';
      del.setAttribute('data-mun-del', ''); del.setAttribute('aria-label', 'remove'); del.innerHTML = '&times;';
      row.appendChild(thumb); row.appendChild(text); row.appendChild(del);
      return row;
    }

    function load() {
      if (!(window.MIEN && MIEN.myUnknowns)) return;
      MIEN.myUnknowns().then(function (rows) {
        listEl.innerHTML = ''; byPath = {};
        rows.forEach(function (r) { listEl.appendChild(rowEl(r)); });
        refreshEmpty();
      });
    }

    if (fileInput) fileInput.addEventListener('change', function () {
      setErr('');
      var f = fileInput.files && fileInput.files[0];
      if (!f) { picked = null; resetPreview(); if (post) post.disabled = true; return; }
      var v = validFile(f);
      if (v) { setErr(v); fileInput.value = ''; picked = null; resetPreview(); if (post) post.disabled = true; return; }
      picked = f;
      try { preview.src = URL.createObjectURL(f); preview.hidden = false; if (dropInner) dropInner.style.display = 'none'; } catch (e) {}
      if (post) post.disabled = false;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!picked) { setErr('pick a photo.'); return; }
      if (!(window.MIEN && MIEN.uploadUnknown)) { setErr('try again in a moment.'); return; }
      var lbl = post ? post.textContent : ''; if (post) { post.disabled = true; post.textContent = 'adding…'; }
      MIEN.uploadUnknown(picked, (note && note.value || '').trim()).then(function (r) {
        if (r && r.error) { if (post) { post.disabled = false; post.textContent = lbl; } setErr((r.error.message || 'try again.')); return; }
        var row = r && r.data ? r.data : null;
        if (row) { if (emptyEl) emptyEl.hidden = true; listEl.insertBefore(rowEl(row), listEl.firstChild); }
        picked = null; if (fileInput) fileInput.value = ''; if (note) note.value = ''; resetPreview();
        if (post) { post.textContent = lbl; post.disabled = true; } setErr('');
      }, function () { if (post) { post.disabled = false; post.textContent = lbl; } setErr('try again.'); });
    });

    listEl.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-mun-del]') : null;
      if (!b) return;
      var row = b.closest('.mun-row'); if (!row) return;
      var id = row.getAttribute('data-id'), path = row.getAttribute('data-path') || null;
      row.remove(); refreshEmpty();
      if (id && window.MIEN && MIEN.deleteUnknown) MIEN.deleteUnknown(id, path);
    });

    if (signinBtn) signinBtn.addEventListener('click', function () {
      if (window.MienAuthModal && MienAuthModal.open) MienAuthModal.open({ mode: 'create', source: 'unknowns' });
    });

    function apply(sess) {
      var signedIn = !!(sess && sess.user);
      if (form) form.hidden = !signedIn;
      if (authEl) authEl.hidden = signedIn;
      if (signedIn) load();
      else { listEl.innerHTML = ''; if (emptyEl) emptyEl.hidden = true; }
    }
    var tries = 0;
    (function boot() {
      if (!(window.MIEN && MIEN.ready && MIEN.ready())) { if (++tries < 60) { setTimeout(boot, 150); return; } if (authEl) authEl.hidden = false; return; }
      MIEN.session().then(apply).catch(function () { if (authEl) authEl.hidden = false; });
      MIEN.onChange(apply);
    })();
  }

  /* ============================================================
     this-or-that onboarding (2026-08-10) — five quick pairs, tap what you'd
     wear. pairs are sampled LIVE from the feed pool along five style axes
     (loud vs neutral, jersey vs denim, hoodie vs collared, outerwear vs tee,
     hats vs bottoms), so the slides always show real, in-stock pieces and
     never a dead image. each tap feeds MienTaste; finishing (or skipping)
     stamps done so it shows exactly once per browser/account.
     ============================================================ */
  /* campaign traffic sees the product first (2026-08-16, before the first paid ad).
     an ad promises clothes; landing on five taps before a single tile is a wall in
     front of the thing that was promised, and it is paid-for attention being spent on
     a survey. deferred for the WHOLE visit via sessionStorage — checking only
     location.search would let the modal ambush them on page 2, once they had navigated
     internally and dropped the utm. nothing is stamped done, so an untagged later visit
     still gets the full onboarding and the taste vector still builds from hearts.
     to revert: delete this function and the `|| ttDeferred()` below. */
  var TT_DEFER_SS = 'mien_tt_defer';
  function ttDeferred() {
    try {
      if (sessionStorage.getItem(TT_DEFER_SS)) return true;
      if (/[?&]utm_/.test(location.search)) { sessionStorage.setItem(TT_DEFER_SS, '1'); return true; }
    } catch (e) {}
    return false;
  }
  function thisOrThatInit() {
    var root = document.querySelector('[data-mtt]');
    if (!root || MienTaste.done() || ttDeferred()) return;
    var optBtns = [root.querySelector('[data-mtt-opt="0"]'), root.querySelector('[data-mtt-opt="1"]')];
    var optImgs = [root.querySelector('[data-mtt-img="0"]'), root.querySelector('[data-mtt-img="1"]')];
    var optBrands = [root.querySelector('[data-mtt-brand="0"]'), root.querySelector('[data-mtt-brand="1"]')];
    var dotsWrap = root.querySelector('[data-mtt-dots]');
    var titleEl = root.querySelector('[data-mtt-title]');
    var kickEl = root.querySelector('[data-mtt-kicker]');
    var saidEl = root.querySelector('[data-mtt-said]');
    var cardEl = root.querySelector('.mtt-card');
    if (!optBtns[0] || !optBtns[1]) return;
    /* each slide: two pools of features to sample from — contrast is the teacher */
    var AXES = [
      [['col:multi', 'col:red', 'col:orange', 'col:pink', 'col:yellow', 'col:purple'], ['col:black', 'col:white', 'col:grey']],
      [['item:jerseys'], ['item:denim']],
      [['item:hoodies', 'item:sweatshirts'], ['item:shirts', 'cat:knitwear']],
      [['item:jackets', 'cat:outerwear'], ['item:tees']],
      [['cat:hats'], ['cat:pants', 'cat:shorts']]
    ];
    /* ---- the voice (2026-08-16) ----------------------------------------------
       it used to ask "which would you wear?" five times, then say thank you. that is a
       form. what it is actually doing is reading you, so it now talks like it: a
       different line each slide, and a VERDICT at the end — "you're loud + denim coded"
       — assembled from the taps that just happened. the payoff has to be a result, not
       a receipt, or the five taps read as a toll on the way to the feed.
       ASK/KICK[0] are duplicated in mien-thisorthat.liquid to stop a first-paint flash. */
    var ASK = [
      'first instinct. which one?',
      'no thinking. which one?',
      'which one do you reach for?',
      'which one ends up in rotation?',
      'last one. which is more you?'
    ];
    var KICK = ['five taps, your mien', 'two of five', 'three of five', 'four of five', 'last one'];
    /* one word per side, in AXES order — the raw material for the verdict */
    var WORD = [['loud', 'quiet'], ['jersey', 'denim'], ['hooded', 'collared'], ['layered', 'tee'], ['hats', 'trousers']];
    /* the colour axis always leads (it is the loudest thing about anyone's taste); the second
       word is whichever of the rest ranks highest here, because "loud + denim" says something
       and "loud + trousers" does not. anything unpicked (thin pool, early close) just isn't in
       the list, so the verdict degrades to one word rather than inventing a taste. */
    var WORD_RANK = ['denim', 'hooded', 'collared', 'jersey', 'layered', 'tee', 'hats', 'trousers'];
    var picked = [];
    function verdict() {
      var lead = null, rest = [];
      picked.forEach(function (w) {
        if (w === 'loud' || w === 'quiet') lead = w; else rest.push(w);
      });
      rest.sort(function (a, b) { return WORD_RANK.indexOf(a) - WORD_RANK.indexOf(b); });
      var words = [];
      if (lead) words.push(lead);
      if (rest.length) words.push(rest[0]);
      if (!words.length) return null;
      return 'you’re ' + words.join(' + ') + ' coded';
    }
    var step = 0, used = {}, pair = [null, null], open = false;
    function tiles() {
      var p = (window.MienFeedPool && window.MienFeedPool()) || null;
      if (p && p.length) return p;
      return [].slice.call(document.querySelectorAll('.mfeed-grid > .mfeed-tile'));
    }
    function tileImgSrc(t) {
      var im = t.querySelector('img');
      return im ? (im.getAttribute('src') || '') : '';
    }
    function matches(t, wants) {
      var f = MienTaste.featuresOf(t);
      for (var i = 0; i < wants.length; i++) if (f.indexOf(wants[i]) !== -1) return true;
      return false;
    }
    function pickFor(wants, avoidHandle) {
      var all = tiles();
      var cands = all.filter(function (t) {
        var h = t.getAttribute('data-handle');
        return h && !used[h] && h !== avoidHandle && tileImgSrc(t) && matches(t, wants);
      });
      if (!cands.length) cands = all.filter(function (t) {
        var h = t.getAttribute('data-handle');
        return h && !used[h] && h !== avoidHandle && tileImgSrc(t);
      });
      if (!cands.length) return null;
      return cands[(Math.random() * cands.length) | 0];
    }
    function paintDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (var i = 0; i < AXES.length; i++) {
        var d = document.createElement('i');
        if (i < step) d.className = 'is-done'; else if (i === step) d.className = 'is-here';
        dotsWrap.appendChild(d);
      }
    }
    function showStep() {
      var axis = AXES[step];
      var a = pickFor(axis[0], null);
      var b = pickFor(axis[1], a ? a.getAttribute('data-handle') : null);
      if (!a || !b) { closeOut(true); return; } /* pool too thin — never block the feed */
      if (titleEl && ASK[step]) titleEl.textContent = ASK[step];
      if (kickEl && KICK[step]) kickEl.textContent = KICK[step];
      pair = [a, b];
      [a, b].forEach(function (t) { used[t.getAttribute('data-handle')] = true; });
      for (var k = 0; k < 2; k++) {
        var t = pair[k];
        if (optImgs[k]) { optImgs[k].src = tileImgSrc(t); }
        if (optBrands[k]) {
          var bEl = t.querySelector('.mfeed-brand');
          optBrands[k].textContent = bEl ? bEl.textContent.trim().toLowerCase() : '';
        }
      }
      paintDots();
    }
    function setOpen(on) {
      open = on;
      root.hidden = !on;
      root.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('mtt-open', on);
    }
    function closeOut(silent) {
      MienTaste.finish();
      if (silent) { setOpen(false); return; }
      var v = verdict();
      if (kickEl) kickEl.textContent = 'your mien';
      if (titleEl) titleEl.textContent = v || 'got it — this feed is yours now';
      if (saidEl) { saidEl.textContent = 'the feed just rearranged itself.'; saidEl.hidden = false; }
      if (cardEl) cardEl.classList.add('is-done');
      try { document.dispatchEvent(new CustomEvent('mien:tastechange')); } catch (e) {}
      /* longer hold than the old 900ms: the verdict is the only part of this they'll
         remember, and it has to be readable before the card gets out of the way. */
      setTimeout(function () { setOpen(false); }, 1700);
    }
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-mtt-skip]')) { closeOut(true); return; }
      var ob = e.target.closest('[data-mtt-opt]');
      if (!ob || !open) return;
      var k = ob.getAttribute('data-mtt-opt') === '0' ? 0 : 1;
      if (!pair[k]) return;
      MienTaste.learn(pair[k], pair[1 - k]);
      /* the word for the side they took — feeds the verdict. only claimed when the tile
         REALLY carries that axis's features: pickFor falls back to any live tile when a
         pool is thin, and a verdict that calls you denim-coded off a hat is a lie. */
      if (WORD[step] && AXES[step] && matches(pair[k], AXES[step][k])) picked.push(WORD[step][k]);
      step++;
      if (step >= AXES.length) closeOut(false);
      else showStep();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && open) closeOut(true); });
    /* ENGAGEMENT GATE (2026-08-24): the card used to open ~800ms after the pool arrived —
       first paint, before a single tile was seen. a first-time visitor now gets the feed
       first: the quiz waits for 30s on site AND two viewport-heights of scrolling, and it
       stands down if the signup modal is already up. skip/esc unchanged. */
    var TT_MIN_MS = 30000, TT_MIN_SCROLLS = 2;
    /* capture-phase on document so it hears the scroll whatever the container is —
       window-level listeners miss element scrolling if css ever makes body the scroller */
    var ttY = function () { return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0; };
    var ttLanded = Date.now(), ttLastY = ttY(), ttTravel = 0;
    document.addEventListener('scroll', function () {
      var y = ttY();
      ttTravel += Math.abs(y - ttLastY);
      ttLastY = y;
    }, { capture: true, passive: true });
    function ttEngaged() {
      return (Date.now() - ttLanded) >= TT_MIN_MS &&
             (ttTravel / Math.max(1, window.innerHeight)) >= TT_MIN_SCROLLS;
    }
    var started = false;
    function start() {
      if (started || MienTaste.done()) return;
      started = true;
      (function attempt() {
        if (MienTaste.done()) return;
        if (!ttEngaged()) { setTimeout(attempt, 3000); return; }
        /* the signup modal owns the screen if it got there first — wait it out */
        var mauth = document.querySelector('.mauth.is-open');
        if (mauth) { setTimeout(attempt, 8000); return; }
        showStep(); setOpen(true);
      })();
    }
    document.addEventListener('mien:feedpool', start);
    if (window.MienFeedPool && window.MienFeedPool()) start();
  }

  function init() { feedInit(); filterBarInit(); tilePageInit(); closetInit(); commentsInit(); tileGalleryInit(); feedFilterInit(); unknownsInit(); thisOrThatInit(); MienTaste.adoptProfile(); setTimeout(MienTaste.adoptProfile, 2500); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
